import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

const translations = {
  en: {
    // Header
    platformTitle: "Research Collaboration Platform",
    about: "About",
    dashboard: "Dashboard",
    services: "Services",
    createProfile: "Create Profile",
    contact: "Contact",
    contactSupport: "Contact Support",
    login: "Login / Sign In",
    logout: "Logout",
    welcome: "Welcome",
    
    // Statistics
    collaborations: "Collaborations",
    visitors: "Visitors",
    researchProjects: "Research Projects",
    registeredUsers: "Registered Users",
    
    // Hero Section
    heroTitle: "Together Toward Deeper",
    heroTitleAccent: " and More Impactful Researches",
    heroDescription: "Join the premier platform connecting researchers worldwide. Share data, collaborate in real-time, and publish breakthrough findings together. Advance your research with intelligent collaboration tools — from project management to AI-assisted paper discovery. Empowering research teams with seamless communication and secure data sharing — all in one dynamic digital hub.",
    getStarted: "Get Started Free",
    
    // Dashboard Section
    dashboardTitle: "Powerful Research Dashboard",
    dashboardDescription: "Everything you need to manage your research projects, collaborate with peers, and track progress in one unified platform.",
    
    // Dashboard Features
    dataManagement: "Data Management",
    dataManagementDesc: "Securely store, organize, and share research data with advanced version control and backup systems.",
    teamCollaboration: "Team Collaboration",
    teamCollaborationDesc: "Work seamlessly with researchers across the globe through real-time editing and communication tools.",
    analyticsInsights: "Analytics & Insights",
    analyticsInsightsDesc: "Visualize your data with powerful analytics tools and generate comprehensive research insights.",
    documentManagement: "Document Management",
    documentManagementDesc: "Create, edit, and manage research papers with built-in citation tools and formatting templates.",
    progressTracking: "Progress Tracking",
    progressTrackingDesc: "Monitor project milestones, deadlines, and team contributions with intuitive dashboards.",
    securityPrivacy: "Security & Privacy",
    securityPrivacyDesc: "Enterprise-grade security with encrypted data storage and customizable access controls.",
    
    // Services Section
    servicesTitle: "Our Services",
    servicesDescription: "Comprehensive solutions designed to support every stage of your research journey.",
    researchProjectManagement: "Research Project Management",
    researchProjectManagementDesc: "Complete project lifecycle management from hypothesis to publication. Track progress, manage collaborators, and organize all your research materials in one place.",
    globalCollaboration: "Global Collaboration Network",
    globalCollaborationDesc: "Connect with researchers worldwide, share findings, and build collaborative partnerships. Our platform facilitates seamless international research cooperation.",
    findResearchPartner: "Find Your Research Partner",
    findResearchPartnerDesc: "Discover and connect with researchers who share your interests and expertise. Our intelligent matching system helps you find the perfect collaborators for your projects.",
    virtualDiscussionRooms: "Virtual Discussion Rooms",
    virtualDiscussionRoomsDesc: "Host or join live virtual meetings with video conferencing, screen sharing, and collaborative whiteboards. Perfect for remote research teams and international partnerships.",
    findResearchAssistant: "Find a Research Assistant",
    findResearchAssistantDesc: "Connect with qualified research assistants to support your projects",
    browseQualifiedAssistants: "Browse qualified assistants",
    viewSkillsExperience: "View skills & experience",
    directCollaboration: "Direct collaboration",
    browseResearchAssistants: "Browse Research Assistants",
    researchInterestsSkills: "Research Interests / Skills",
    describeInterestsSkills: "Describe your research interests, areas of expertise, and skills...",
    researchField: "Research Field",
    describeResearchField: "Describe your research field...",
    includeResearchAreas: "Include your areas of research, methodologies, technical skills, and expertise",
    iAm: "I am a",
    selectYourRole: "Select your role",
    researcher: "Researcher",
    researchAssistant: "Research Assistant",
    both: "Both",
    roleRequired: "Role Required",
    selectRoleMessage: "Please select your role (Researcher, Research Assistant, or Both)",
    
    // Service Features
    projectTimeline: "Project timeline planning",
    resourceAllocation: "Resource allocation",
    budgetTracking: "Budget tracking",
    researcherDirectory: "Researcher directory",
    realtimeCollab: "Real-time collaboration tools",
    secureDataSharing: "Secure data sharing",
    smartMatching: "Smart matching algorithm",
    browseInterests: "Browse by research interests",
    directMessaging: "Direct messaging & invitations",
    hdVideo: "HD video & audio conferencing",
    screenSharing: "Screen sharing & recording",
    interactiveWhiteboard: "Interactive whiteboard",
    
    // Additional Service Features
    fileSharingCollab: "File sharing & collaboration",
    onlineDocEditing: "Online document editing",
    accessTimeTracking: "Access time tracking",
    manageYourProjects: "Manage Your Projects",
    researcherLibraryDesc: "Upload, organize, and analyze your research papers with AI. Get instant summaries, generate citations, and manage your academic library efficiently.",
    pdfUploadManagement: "PDF upload & management",
    aiPoweredSummarization: "AI-powered summarization",
    citationGeneration: "Citation generation (APA, MLA, etc.)",
    paperAnalysisInsights: "Paper analysis & insights",
    openLibrary: "Open Library",
    browseResearchers: "Browse Researchers",
    joinVirtualRooms: "Join Virtual Rooms",
    
    // Virtual Rooms - Jitsi Integration
    participants: "Participants",
    plan: "Plan",
    remaining: "remaining",
    unlimited: "Unlimited",
    sessionEndingSoon: "Session Ending Soon",
    timeWarning: "Time Warning",
    minutesRemaining: "minutes remaining",
    sessionTimeExpired: "Session Time Expired",
    sessionWillEndAutomatically: "Session will end automatically",
    connecting: "Connecting to video room...",
    goBack: "Go Back",
    sessionActive: "Session Active",
    sessionInactive: "Session Inactive",
    privateRoom: "Private Room",
    leaveRoom: "Leave Room",
    
    // Virtual Rooms Page
    virtualRoomsPageTitle: "Virtual Discussion Rooms",
    virtualRoomsPageDesc: "Connect with researchers worldwide through HD video conferencing. Host or join live discussions, share screens, and collaborate in real-time.",
    allTimesInAST: "All times displayed in Arabia Standard Time (AST / UTC+3)",
    createNewRoom: "Create New Room",
    mySchedule: "My Schedule",
    createVirtualRoom: "Create Virtual Discussion Room",
    setupVideoConference: "Set up a new video conference for your research team",
    timesInAST: "Times are in Arabia Standard Time (AST / UTC+3)",
    roomType: "Room Type",
    public: "Public",
    anyoneCanJoin: "Anyone can join",
    private: "Private",
    inviteOnly: "Invite only",
    selectParticipants: "Select Participants",
    selectOneOrMore: "Select one or more researchers to invite to this private room",
    roomTitle: "Room Title",
    roomTitlePlaceholder: "e.g., Weekly AI Research Discussion",
    description: "Description",
    descriptionPlaceholder: "Brief description of the meeting",
    date: "Date",
    time: "Time",
    durationMinutes: "Duration (minutes)",
    createRoom: "Create Room",
    hdVideo: "HD Video",
    crystalClearVideo: "Crystal clear video quality",
    screenShare: "Screen Share",
    presentYourResearch: "Present your research",
    liveChat: "Live Chat",
    realtimeMessaging: "Real-time messaging",
    upTo50Users: "Up to 50 Users",
    largeTeamMeetings: "Large team meetings",
    liveRooms: "Live Rooms",
    loadingRooms: "Loading rooms...",
    noLiveRooms: "No live rooms at the moment",
    createRoomToCollaborate: "Create a room to start collaborating!",
    live: "LIVE",
    host: "Host",
    minutes: "min",
    joinNow: "Join Now",
    joining: "Joining...",
    privateRoom: "Private Room",
    upcomingSessions: "Upcoming Sessions",
    noUpcomingSessions: "No upcoming sessions scheduled",
    beFirstToCreate: "Be the first to create one!",
    register: "Register",
    startCollaboratingToday: "Start Collaborating Today",
    createOrJoinRooms: "Create your own discussion room or join existing sessions to connect with researchers worldwide",
    findCollaborators: "Find Collaborators",
    roomCreatedSuccess: "Room Created Successfully",
    roomReadyMessage: "Your {type} discussion room is ready!",
    joiningRoom: "Joining Room",
    connectingTo: "Connecting to \"{title}\"...",
    accessDenied: "Access Denied",
    privateRoomNotInvited: "This is a private room and you're not invited",
    pleaseCreateProfile: "Please create a researcher profile first",
    selectAtLeastOne: "Please select at least one participant for the private room",
    failedToCreateRoom: "Failed to create room. Please try again.",
    failedToJoinRoom: "Failed to join room",
    virtualRoomsLoginRequired: "Please sign in to access virtual discussion rooms",
    
    // Profile Section
    profileTitle: "Create Your Researcher Profile",
    profileDescription: "Join our global community of researchers. Create your profile to start collaborating, sharing research, and finding partners.",
    buildProfile: "Build Your Professional Profile",
    buildProfileDesc: "Share your research interests and connect with peers worldwide",
    fullName: "Full Name",
    email: "Email",
    country: "Country",
    city: "City",
    university: "University / Institution",
    phoneNumber: "Phone Number",
    researchInterests: "Research Interests",
    researchInterestsPlaceholder: "Describe your research areas, expertise, and interests... (e.g., Machine Learning, Quantum Computing, Biotechnology)",
    researchInterestsHelper: "This helps other researchers find and connect with you",
    createProfileBtn: "Create Profile",
    viewAllProfiles: "View All Profiles",
    
    // Profile Form - Additional Labels
    signInRequired: "Sign In Required",
    signInToCreateProfile: "Please sign in to create your researcher profile and join our community",
    signInToCreateProfileBtn: "Sign In to Create Profile",
    profilePhotoOptional: "Profile Photo (Optional)",
    uploadPhotoHelper: "Upload a professional photo (Max 5MB, JPG/PNG) - Optional",
    fullNameRequired: "Full Name *",
    emailRequired: "Email *",
    countryRequired: "Country *",
    cityRequired: "City *",
    universityRequired: "University / Institution *",
    phoneOptional: "Phone Number",
    researchInterestsRequired: "Research Interests *",
    googleScholarOptional: "Google Scholar URL (Optional)",
    googleScholarPlaceholder: "https://scholar.google.com/citations?user=...",
    googleScholarHelper: "Link to your Google Scholar profile",
    orcidOptional: "ORCID iD (Optional)",
    orcidPlaceholder: "0000-0000-0000-0000",
    orcidHelper: "Your unique researcher identifier",
    createProfileDesc: "Join our global community of researchers and find the perfect collaborators",
    
    // Profile Form Placeholders
    namePlaceholder: "Dr. Jane Smith",
    emailPlaceholder: "jane.smith@university.edu",
    countryPlaceholder: "United States",
    cityPlaceholder: "Boston",
    universityPlaceholder: "Massachusetts Institute of Technology",
    phonePlaceholder: "+1 (555) 123-4567",
    interestsPlaceholder: "Describe your research areas, expertise, and interests... (e.g., Machine Learning, Quantum Computing, Biotechnology)",
    interestsHelper: "This helps other researchers find and connect with you",
    
    // Profile Benefits
    getDiscovered: "Get Discovered",
    getDiscoveredDesc: "Make it easy for researchers to find you based on your expertise and interests",
    buildNetwork: "Build Network",
    buildNetworkDesc: "Connect with peers, collaborators, and potential research partners worldwide",
    growImpact: "Grow Impact",
    growImpactDesc: "Increase your research visibility and collaborate on breakthrough projects",
    
    // Contact Section
    getInTouch: "Get in Touch",
    getInTouchDesc: "Have questions about our platform? Our team is here to help you get started with your research collaboration journey.",
    emailLabel: "Email",
    whatsappLabel: "WhatsApp",
    sendMessage: "Send us a Message",
    sendMessageDesc: "We'll respond within 24 hours",
    nameLabel: "Name",
    messageLabel: "Message",
    messagePlaceholder: "Tell us about your research needs...",
    sendBtn: "Send Message",
    
    // Footer
    footerTagline: "Empowering researchers to collaborate and innovate for a better tomorrow.",
    platform: "Platform",
    resources: "Resources",
    legal: "Legal",
    documentation: "Documentation",
    apiReference: "API Reference",
    support: "Support",
    blog: "Blog",
    pricing: "Pricing",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    cookiePolicy: "Cookie Policy",
    gdpr: "GDPR",
    copyright: "© 2025 RCP. All rights reserved.",
    
    // Auth
    loginTitle: "Login to RCP",
    loginDesc: "Enter your credentials to access your research dashboard",
    createAccountTitle: "Create Account",
    createAccountDesc: "Join our research community today",
    name: "Name",
    password: "Password",
    forgotPassword: "Forgot password?",
    loginBtn: "Login",
    signUpBtn: "Sign Up",
    alreadyHaveAccount: "Already have an account? ",
    dontHaveAccount: "Don't have an account? ",
    signUpLink: "Sign up",
    loginLink: "Login",
    
    // Auth Dialog - Additional
    emailPlaceholderAuth: "researcher@university.edu",
    passwordPlaceholderCreate: "Create a strong password",
    passwordPlaceholderEnter: "Enter your password",
    completeResearcherProfile: "Complete Your Researcher Profile",
    completeProfileDesc: "Fill in your details to create your researcher profile. Fields marked with * are required.",
    universityInstitution: "University/Institution *",
    phoneOptionalShort: "Phone (Optional)",
    describeInterestsPlaceholder: "Describe your research interests and areas of expertise...",
    cancel: "Cancel",
    
    // Toast Messages
    loginSuccess: "Login Successful",
    loginSuccessDesc: "Welcome back to RCP!",
    accountCreated: "Account Created",
    accountCreatedDesc: "Welcome to RCP!",
    messageSent: "Message Sent",
    profileCreated: "Profile Created Successfully",
    profileCreatedDesc: "Redirecting to researcher profiles...",
    loggedOut: "Logged Out",
    loggedOutDesc: "You have been successfully logged out.",
    
    // Pricing Section
    pricingTitle: "Simple and Transparent Pricing",
    pricingDesc: "Choose the plan that works best for you",
    monthly: "Monthly",
    yearly: "Yearly",
    saveUpTo: "Save up to",
    save20: "Save 20%",
    perMonth: "/ month",
    perYear: "/ year",
    basic: "Basic",
    premium: "Premium",
    advanced: "Advanced",
    mostPopular: "Most Popular",
    forIndividuals: "Perfect for getting started",
    forResearchers: "For individual researchers",
    forPowerUsers: "For power users",
    getStartedBtn: "Get Started",
    startPremium: "Start Premium",
    subscribeNow: "Subscribe Now",
    allPlansInclude: "All plans include secure data storage and regular backups",
    needCustomPlan: "Need a custom plan?",
    contactUs: "Contact us",
    forEnterprise: "for enterprise solutions",
    
    // Pricing Features
    aiCredits: "AI credits per month",
    researchProjects: "research projects",
    storage: "storage",
    virtualRooms: "virtual rooms",
    basicVirtualRooms: "4 rooms/month (30 min each)",
    
    // Storage Display
    storageUsage: "Storage Usage",
    of: "of",
    left: "left",
    used: "Used",
    libraryDocuments: "Library Documents",
    projectFiles: "Project Files",
    basicPlan: "Basic Plan",
    premiumPlan: "Premium Plan",
    advancedPlan: "Advanced Plan",
    gb: "GB",
    mb: "MB",
    kb: "KB",
    bytes: "Bytes",
    unlimitedVirtualRooms: "Unlimited rooms (unlimited time)",
    unlimitedRoomsRecording: "Unlimited rooms with recording",
    communitySupport: "Community support",
    prioritySupport: "Priority email support",
    support247: "24/7 priority support",
    basicDocManagement: "Basic document management",
    fiftyAICredits: "50 AI credits per month",
    threeResearchProjects: "3 research projects",
    fiveGBStorage: "5 GB storage",
    basicRooms: "4 rooms/month (30 min each)",
    fiveHundredAICredits: "500 AI credits per month",
    fiftyGBStorage: "50 GB storage",
    twoThousandFiveHundredAICredits: "2,500 AI credits per month",
    fiveHundredGBStorage: "500 GB storage",
    advancedAI: "Advanced AI analysis",
    collaborationTools: "Collaboration tools",
    exportFormats: "Export in multiple formats",
    tenResearchProjects: "10 research projects",
    extendedVirtualRooms: "10 rooms/month (2 hrs each)",
    realtimeCollaboration: "Real-time collaboration",
    documentSharingVersioning: "Document sharing & versioning",
    unlimitedProjectsCollaborators: "Unlimited projects & collaborators",
    unlimitedProjects: "Unlimited research projects",
    unlimitedTeamMembers: "Unlimited projects & team members",
    dedicatedAccountManager: "Dedicated account manager",
    customIntegrations: "Custom integrations",
    apiAccess: "API access",
    advancedAnalytics: "Advanced analytics",
    advancedAnalyticsDashboard: "Advanced analytics dashboard",
    apiAccessIntegrations: "API access for integrations",
    customDataExport: "Custom data export options",
    enhancedSecurity: "Enhanced security features",
    dedicatedSupportChannel: "Dedicated support channel",
    whiteLabelOptions: "White-label options",
    ssoIntegration: "SSO (Single Sign-On)",
    
    // Pricing Additional
    saveYearly: "Save $24/year (90 SAR)",
    saveYearlyAdvanced: "Save $120/year (450 SAR)",
    allPlansIncludeStorage: "All plans include secure data storage and regular backups",
    needCustomPlan: "Need a custom plan?",
    contactUs: "Contact us",
    forEnterpriseSolutions: "for enterprise solutions",
    
    // Community Forum
    communityForum: "Community Forum",
    announcements: "Announcements",
    announcementsDesc: "Stay updated with the latest news, events, and important updates from the research community.",
    clickToView: "Click to View",
    createAnnouncement: "Create Announcement",
    createAnnouncementDesc: "Share important news and updates with the research community",
    editAnnouncement: "Edit Announcement",
    deleteAnnouncement: "Delete Announcement",
    confirmDeleteAnnouncement: "Are you sure you want to delete this announcement? This action cannot be undone.",
    searchAnnouncements: "Search announcements...",
    noAnnouncements: "No Announcements Yet",
    beFirstToPost: "Be the first to post an announcement",
    signInToView: "Please sign in to view announcements",
    enterTitle: "Enter announcement title",
    enterContent: "Enter announcement content",
    image: "Image",
    optional: "Optional",
    maxImageSize: "Maximum image size:",
    uploading: "Uploading...",
    recentAnnouncements: "Recent Announcements",
    stayUpdatedWithLatest: "Stay updated with the latest news and updates from our community",
    noAnnouncementsYet: "No announcements yet",
    viewAllAnnouncements: "View All Announcements",
    announcement: "Announcement",
    new: "New",
    joinVibrantCommunity: "Join our vibrant community of researchers. Ask questions, share insights, and collaborate on cutting-edge research topics",
    askAnswerQuestions: "Ask & Answer Questions",
    askAnswerQuestionsDesc: "Post your research questions and get expert answers from fellow researchers in your field",
    shareKnowledge: "Share Knowledge",
    shareKnowledgeDesc: "Contribute your expertise and help advance research by sharing insights and best practices",
    connectWithExperts: "Connect with Experts",
    connectWithExpertsDesc: "Network with researchers worldwide and build meaningful collaborations",
    engageReact: "Engage & React",
    engageReactDesc: "Use emoji reactions to show appreciation and highlight valuable contributions",
    recentDiscussions: "Recent Discussions",
    joinTheDiscussion: "Join the Discussion",
    views: "views",
    answers: "answers",
    live: "Live",
    general: "General",
    welcomeToRC: "Welcome to the RC Platform Community!",
    beFirstToDiscuss: "Be the first to start a discussion!",
    
    // Community Forum Page
    connectShareCollaborate: "Connect, share knowledge, and collaborate with researchers",
    askQuestion: "Ask Question",
    searchQuestions: "Search questions...",
    category: "Category",
    sortBy: "Sort by",
    newestFirst: "Newest First",
    oldestFirst: "Oldest First",
    mostPopular: "Most Popular",
    unanswered: "Unanswered",
    noQuestionsFound: "No questions found. Be the first to ask!",
    loadingForum: "Loading forum...",
    answered: "Answered",
    postQuestion: "Post Question",
    postAnswer: "Post Answer",
    yourAnswer: "Your Answer",
    shareYourKnowledge: "Share your knowledge and help the community...",
    answer: "Answer",
    backToForum: "Back to Forum",
    editPost: "Edit Post",
    deletePost: "Delete Post",
    editAnswer: "Edit Answer",
    deleteAnswer: "Delete Answer",
    updatePost: "Update Post",
    updateAnswer: "Update Answer",
    deleteConfirmPost: "Are you sure you want to delete this post? This action cannot be undone.",
    deleteConfirmAnswer: "Are you sure you want to delete this answer? This action cannot be undone.",
    updateYourQuestion: "Update your question or post",
    updateYourAnswer: "Update your answer",
    title: "Title",
    whatsYourQuestion: "What's your question?",
    details: "Details",
    provideMoreDetails: "Provide more details about your question...",
    shareKnowledgePlaceholder: "Share your knowledge...",
    askQuestionTitle: "Ask a Question",
    shareQuestionWithCommunity: "Share your question with the research community",
    fillAllFields: "Please fill in all fields",
    enterAnswer: "Please enter an answer",
    postCreatedSuccess: "Post created successfully!",
    postUpdatedSuccess: "Post updated successfully!",
    postDeletedSuccess: "Post deleted successfully!",
    answerPostedSuccess: "Answer posted successfully!",
    answerUpdatedSuccess: "Answer updated successfully!",
    answerDeletedSuccess: "Answer deleted successfully!",
    failedToCreatePost: "Failed to create post",
    failedToUpdatePost: "Failed to update post",
    failedToDeletePost: "Failed to delete post",
    failedToPostAnswer: "Failed to post answer",
    failedToUpdateAnswer: "Failed to update answer",
    failedToDeleteAnswer: "Failed to delete answer",
    researcherProfileRequired: "Researcher Profile Required",
    needProfileForForum: "You need to create a researcher profile to access the community forum and participate in discussions.",
    needProfileForLibrary: "You need to create a researcher profile to access the library and manage your documents.",
    profileNotFound: "Profile Not Found",
    errorLoadingProfile: "Error Loading Profile",
    unableToLoadProfile: "Unable to load your researcher profile. Please try again or check your connection.",
    retry: "Retry",
    createProfile: "Create Profile",
    goHome: "Go Home",
    authenticationRequired: "Authentication Required",
    signInToAccessLibrary: "Please sign in to access the library and manage your research documents.",
    createResearcherProfile: "Create Researcher Profile",
    joinCommunityForum: "Join the Community Forum",
    signInToAccessForum: "Please sign in to access the community forum and connect with researchers.",
    signIn: "Sign In",
    bestAnswer: "Best Answer",
    like: "Like",
    love: "Love",
    insightful: "Insightful",
    helpful: "Helpful",
    thinking: "Thinking",
    
    // Forum Categories
    allTopics: "All Topics",
    generalDiscussion: "General Discussion",
    researchMethods: "Research Methods",
    dataAnalysis: "Data Analysis",
    literatureReview: "Literature Review",
    careerAcademia: "Career & Academia",
    toolsSoftware: "Tools & Software",
    collaborationOpportunities: "Collaboration Opportunities",
    feedbackReview: "Feedback & Review",
    
    // Virtual Rooms - Extended Features
    currentPlan: "Current Plan",
    sessionLimit: "Session Limit",
    roomTitle: "Room Title", 
    enterRoomTitle: "Enter room title...",
    optionalDescription: "Optional description...",
    plannedDuration: "Planned Duration",
    minutes: "minutes",
    hour: "hour",
    hours: "hours",
    roomPrivacy: "Room Privacy",
    publicRoom: "Public Room",
    anyoneCanJoin: "Anyone can join",
    inviteOnly: "Invite only",
    inviteParticipants: "Invite Participants",
    createRoom: "Create Room",
    createNewRoom: "Create New Room",
    createRoomDescription: "Set up a new virtual discussion room for collaboration",
    roomCreatedSuccessfully: "Room created successfully!",
    failedToCreateRoom: "Failed to create room",
    roomLimitReached: "Room Limit Reached",
    monthlyRoomLimitExceeded: "You have reached your monthly room limit",
    roomsUsedThisMonth: "Rooms used this month",
    roomsThisMonth: "rooms this month",
    monthlyRoomLimit: "Monthly Room Limit",
    roomsPerMonth: "rooms/month",
    accessDenied: "Access Denied",
    privateRoomAccessDenied: "You don't have permission to join this private room",
    failedToJoinRoom: "Failed to join room",
    sessionEnded: "Session Ended",
    sessionEndedSuccessfully: "Session ended successfully",
    liveRooms: "Live Rooms",
    active: "Active",
    live: "LIVE",
    joinNow: "Join Now",
    joining: "Joining...",
    upcomingSessions: "Upcoming Sessions", 
    scheduled: "Scheduled",
    upcoming: "Upcoming",
    startsAt: "Starts at",
    hostedBy: "Hosted by",
    duration: "Duration",
    noRoomsYet: "No rooms yet",
    beFirstToCreateRoom: "Be the first to create a virtual discussion room",
    createFirstRoom: "Create First Room",
    upgradeForLongerSessions: "Upgrade for Longer Sessions",
    getCurrentLimitUpgradeMessage: "Basic plan limits sessions to 30 minutes. Upgrade for longer discussions!",
    upgradePlan: "Upgrade Plan",
    selectAtLeastOne: "Please select at least one participant for private rooms",
    pleaseCreateProfile: "Please create a researcher profile first",
    profileRequired: "Profile Required",
    virtualRoomsLoginRequired: "Please sign in to access virtual discussion rooms",
    participantsSelected: "participants selected",
    creating: "Creating...",
    roomDeletedSuccessfully: "Room deleted successfully",
    roomUpdatedSuccessfully: "Room updated successfully",
    failedToUpdateRoom: "Failed to update room",
    editRoom: "Edit Room",
    editRoomDescription: "Update the room settings",
    saveChanges: "Save Changes",
    failedToDeleteRoom: "Failed to delete room",
    confirmDeleteRoom: "Are you sure you want to delete the room",
    
    // Subscription Page
    subscription: "Subscription",
    manageSubscription: "Manage your subscription and view your plan details",
    basicPlan: "Basic Plan",
    premiumPlan: "Premium Plan",
    advancedPlan: "Advanced Plan",
    freePlan: "Free Plan",
    activeSubscription: "Active Subscription",
    cancelled: "Cancelled",
    pendingPayment: "Pending Payment",
    upgradePlan: "Upgrade Plan",
    changePlan: "Change Plan",
    cancelSubscription: "Cancel Subscription",
    cancelling: "Cancelling...",
    choosePlan: "Choose Your Plan",
    selectPerfectPlan: "Select the perfect plan for your research needs",
    currentPlanLabel: "Current Plan",
    currentPlan: "Current Plan",
    status: "Status",
    month: "month",
    nextBilling: "Next billing",
    forIndividuals: "For individuals",
    forPowerUsers: "For power users",
    subscribeNow: "Subscribe Now",
    getStartedFree: "Get Started Free",
    securePaymentPayPal: "Secure Payment with PayPal",
    safeSecurePayment: "Safe and secure payment processing",
    cancelAnytime: "Cancel anytime - no long-term commitment",
    automaticBilling: "Automatic billing on the same date each month",
    instantAccess: "Instant access to features upon subscription",
    backToHome: "Back to Home",
    processingPayment: "Processing...",
    paymentSuccessTitle: "Payment Successful!",
    paymentSuccessDesc: "Your subscription is now active.",
    sessionExpired: "Session Expired",
    tryAgain: "Please try again.",
    loginRequired: "Login Required",
    pleaseLoginToSubscribe: "Please login to subscribe.",
    paymentCancelled: "Payment Cancelled",
    paymentCancelledDesc: "You cancelled the payment process.",
    purchaseSuccessful: "Purchase Successful!",
    creditsAdded: "credits added to your account",
    paymentFailed: "Payment Failed",
    pleaseWait: "Please wait while we process your payment...",
    error: "Error",
    purchaseFailed: "Purchase Failed",
    
    // Library
    researcherLibrary: "Researcher Library",
    aiCreditsLabel: "AI Credits",
    aiCreditsLoading: "AI Credits (Profile loading...)",
    purchaseAICredits: "Purchase AI Credits",
    getMoreCredits: "Get more credits to continue using AI features",
    runningLowCredits: "Running low on AI credits!",
    creditsRemainingMessage: "You have {count} credits remaining. Consider purchasing more or upgrading your plan.",
    creditsPerMonth: "credits/month",
    credits: "credits",
    total: "Total",
    purchaseCredits: "Purchase Credits",
    upgrade: "Upgrade",
    free: "Free",
    plan: "Plan",
    buyCredits: "Buy Credits",
    uploadPDF: "Upload PDF",
    myDocuments: "My Documents",
    searchDocuments: "Search documents...",
    noDocuments: "No documents uploaded yet",
    uploadFirst: "Upload your first PDF to get started",
    summarize: "Summarize",
    chat: "Chat",
    keywords: "Keywords",
    citation: "Citation",
    rename: "Rename",
    delete: "Delete",
    download: "Download",
    processing: "Processing...",
    
    // Folders
    folders: "Folders",
    createFolder: "Create Folder",
    newFolder: "New Folder",
    folderName: "Folder Name",
    enterFolderName: "Enter folder name",
    createNewFolder: "Create New Folder",
    renameFolder: "Rename Folder",
    deleteFolder: "Delete Folder",
    moveToFolder: "Move to Folder",
    moveTo: "Move to",
    selectFolder: "Select Folder",
    rootFolder: "Root (No Folder)",
    folderCreated: "Folder Created",
    folderCreatedSuccess: "Folder has been created successfully",
    folderRenamed: "Folder Renamed",
    folderRenamedSuccess: "Folder has been renamed successfully",
    folderDeleted: "Folder Deleted",
    folderDeletedSuccess: "Folder has been deleted successfully",
    folderShared: "Folder Shared",
    folderSharedSuccess: "Folder has been shared successfully",
    documentMoved: "Document Moved",
    documentMovedSuccess: "Document has been moved successfully",
    confirmDeleteFolder: "Are you sure you want to delete this folder?",
    folderNotEmpty: "This folder contains documents or subfolders",
    emptyFolderFirst: "Please empty the folder before deleting",
    shareFolder: "Share Folder",
    sharedFolders: "Shared Folders",
    folderPermissions: "Folder Permissions",
    viewOnly: "View Only",
    canEdit: "Can Edit",
    sharedWith: "Shared With",
    selectResearchers: "Select Researchers",
    noFolders: "No folders yet",
    organizeDocs: "Organize your documents by creating folders",
    createRootFolderDesc: "Create a new folder to organize your documents",
    createSubfolderDesc: "Create a subfolder inside the selected folder",
    
    // Dashboard
    researcherDashboard: "Researcher Dashboard",
    home: "Home",
    logout: "Logout",
    projects: "Projects",
    library: "Library",
    notifications: "Notifications",
    messages: "Messages",
    groups: "Groups",
    groupMessages: "Group Messages",
    createGroup: "Create Group",
    groupName: "Group Name",
    enterGroupName: "Enter group name",
    selectMembers: "Select Members",
    addMembers: "Add Members",
    groupCreated: "Group Created",
    groupCreatedSuccess: "Group has been created successfully",
    leaveGroup: "Leave Group",
    deleteGroup: "Delete Group",
    confirmLeaveGroup: "Are you sure you want to leave this group?",
    confirmDeleteGroup: "Are you sure you want to delete this group? All messages will be lost.",
    members: "Members",
    member: "Member",
    you: "You",
    creator: "Creator",
    sendMessage: "Send Message",
    typeMessage: "Type a message...",
    noGroups: "No Groups",
    noGroupsDesc: "You haven't joined any groups yet. Create a group to start collaborating!",
    groupDeleted: "Group Deleted",
    leftGroup: "Left Group",
    memberAdded: "Member Added",
    memberRemoved: "Member Removed",
    myConnections: "My Connections",
    myProjects: "My Projects",
    noNotifications: "No notifications yet",
    stayTuned: "You're all caught up! Stay tuned for updates.",
    noMessages: "No messages yet",
    selectConnection: "Select a connection to start messaging",
    sendMessage: "Send Message",
    typeMessage: "Type your message...",
    messageSent: "Message Sent",
    messageDelivered: "Your message has been delivered",
    error: "Error",
    failedToSend: "Failed to send message",
    noConnections: "No connections yet",
    startConnecting: "Start connecting with other researchers to collaborate",
    browseResearchers: "Browse Researchers",
    noProjects: "No active projects",
    createProject: "Create your first project to get started",
    viewAllProjects: "View All Projects",
    newProject: "New Project",
    dueIn: "Due in",
    days: "days",
    overdue: "Overdue",
    noProfileFound: "No Researcher Profile Found",
    createProfileFirst: "Please create a researcher profile first",
    goToHome: "Go to Home",
    connectionRequests: "Connection Requests",
    acceptRequest: "Accept",
    declineRequest: "Decline",
    noRequests: "No pending requests",
    requestAccepted: "Request Accepted",
    connectionEstablished: "Connection established successfully",
    requestDeclined: "Request Declined",
    
    // Projects Page
    myProjectsTitle: "My Research Projects",
    createNewProject: "Create New Project",
    searchProjects: "Search projects...",
    noProjectsYet: "No projects yet",
    createFirstProject: "Create your first project to start collaborating",
    projectTitle: "Project Title",
    projectDescription: "Project Description",
    startDate: "Start Date",
    endDate: "End Date",
    status: "Status",
    active: "Active",
    completed: "Completed",
    planning: "Planning",
    onHold: "On Hold",
    cancelled: "Cancelled",
    createProject: "Create Project",
    cancel: "Cancel",
    members: "Members",
    tasks: "Tasks",
    files: "Files",
    progress: "Progress",
    dueDate: "Due Date",
    overdue: "Overdue",
    projectCreated: "Project Created",
    projectCreatedSuccess: "Your project has been created successfully",
    projectDeleted: "Project Deleted",
    projectDeletedSuccess: "Project has been deleted",
    confirmDelete: "Are you sure you want to delete this project?",
    deleteProject: "Delete Project",
    editProject: "Edit Project",
    editProjectDescription: "Update your project information",
    updateProject: "Update Project",
    projectUpdated: "Project Updated",
    projectUpdatedMessage: "The project has been updated successfully.",
    updateFailed: "Update Failed",
    failedToUpdateProject: "Failed to update project. Please try again.",
    viewDetails: "View Details",
    activeProjects: "Active Projects",
    totalProjects: "Total Projects",
    currentlyInProgress: "Currently in progress",
    successfullyFinished: "Successfully finished",
    projectsYouCollaborate: "Projects you collaborate on",
    loading: "Loading",
    of: "of",
    
    // Project Detail Page
    overview: "Overview",
    timeline: "Timeline",
    documents: "Documents",
    addTask: "Add Task",
    editTask: "Edit Task",
    taskTitle: "Task Title",
    taskDescription: "Task Description",
    assignee: "Assignee",
    assignTo: "Assign to",
    priority: "Priority",
    high: "High",
    medium: "Medium",
    low: "Low",
    addMilestone: "Add Milestone",
    milestone: "Milestone",
    uploadDocument: "Upload Document",
    documentName: "Document Name",
    uploadedBy: "Uploaded by",
    inviteMember: "Invite Member",
    removeMember: "Remove Member",
    role: "Role",
    permissions: "Permissions",
    owner: "Owner",
    editor: "Editor",
    viewer: "Viewer",
    recentActivity: "Recent Activity",
    comments: "Comments",
    addComment: "Add Comment",
    writeComment: "Write a comment...",
    projectInformation: "Project Information",
    researchArea: "Research Area",
    collaborators: "Collaborators",
    completionRate: "Completion Rate",
    tasksCompleted: "Tasks Completed",
    allYourProjects: "All your projects",
    projectsYouJoined: "Projects you joined",
    collaboratorCount: "collaborator(s)",
    fileCount: "file(s)",
    lastAccess: "Last access",
    you: "You",
    projectTitlePlaceholder: "e.g., AI-Powered Cancer Detection Study",
    loginRequiredMessage: "Please sign in to access research projects",
    confirmDeleteMessage: "Are you sure you want to delete this project? This action cannot be undone.",
    projectDeletedMessage: "The project has been permanently deleted.",
    deleteFailed: "Delete Failed",
    errorTitle: "Error",
    createProfileFirst: "Please create a researcher profile first",
    projectCreatedSuccessTitle: "Project Created Successfully",
    projectReadyMessage: "Your research project is ready!",
    failedToCreateProject: "Failed to create project. Please try again.",
    
    aiSummary: "AI Summary",
    chatWithDocument: "Chat with Document",
    askQuestion: "Ask a question about this document...",
    predefinedQuestions: "Predefined Questions",
    
    // Project Detail - Toast Messages
    accessDenied: "Access Denied",
    noPermissionToView: "You don't have permission to view this project",
    projectNotFound: "Project Not Found",
    projectDoesNotExist: "The project you're looking for doesn't exist",
    failedToLoadProject: "Failed to load project",
    noFileSelected: "No File Selected",
    selectFileToUpload: "Please select a document file to upload",
    documentUploaded: "Document Uploaded",
    documentUploadedSuccess: "Document has been uploaded successfully",
    uploadFailed: "Upload Failed",
    failedToUploadDocument: "Failed to upload document",
    documentDeleted: "Document Deleted",
    documentDeletedSuccess: "Document has been deleted successfully",
    taskCompleted: "Task Completed",
    taskReopened: "Task Reopened",
    taskMarkedAsDone: "Great job! Task marked as done.",
    taskMarkedAsIncomplete: "Task marked as incomplete.",
    taskDeleted: "Task Deleted",
    taskDeletedMessage: "Task has been deleted successfully.",
    confirmDeleteTask: "Are you sure you want to delete this task?",
    couldNotDeleteTask: "Could not delete task. Please try again.",
    deleteTask: "Delete Task",
    updateFailed: "Update Failed",
    couldNotUpdateTask: "Could not update task status",
    selectAtLeastOneFile: "Please select at least one file",
    fileTooLarge: "File Too Large",
    exceedsLimit: "exceeds 100MB limit",
    filesUploadedSuccess: "Files Uploaded Successfully",
    uploadedFiles: "Uploaded",
    someFilesFailedToUpload: "Some files failed to upload",
    selectAtLeastOneResearcher: "Please select at least one researcher to invite",
    collaboratorsInvited: "Collaborators Invited",
    failedToInviteCollaborators: "Failed to invite collaborators",
    fileDeleted: "File Deleted",
    successfullyDeleted: "Successfully deleted",
    collaboratorRemoved: "Collaborator Removed",
    successfullyRemoved: "Successfully removed",
    removeFailed: "Remove Failed",
    roleUpdated: "Role Updated",
    changedRoleTo: "Changed role to",
    roleUpdateFailed: "Role Update Failed",
    downloadFailed: "Download Failed",
    unableToDownload: "Unable to download the file",
    taskCreated: "Task Created",
    taskAddedToProject: "Task has been added to the project",
    taskTitleRequired: "Task title is required",
    failedToCreateTask: "Failed to create task",
    notSpecified: "Not specified",
    backToProjects: "Back to Projects",
    settings: "Settings",
    loadingProject: "Loading project...",
    projectStatistics: "Project Statistics",
    teamMembers: "Team Members",
    projectDocuments: "Project Documents",
    uploadPreviewDocuments: "Upload and preview Word, Excel, and PowerPoint documents",
    projectTasks: "Project Tasks",
    reopen: "Reopen",
    done: "Done",
    due: "Due",
    invite: "Invite",
    taskStatusDistribution: "Task Status Distribution",
    taskStatistics: "Task Statistics",
    inProgress: "In Progress",
    pending: "Pending",
    totalTasks: "Total Tasks",
    taskProgressOverview: "Task Progress Overview",
    projectTimelineTaskProgress: "Project Timeline & Task Progress",
    visualRepresentation: "Visual representation of tasks and their completion status",
    detailedTaskTimeline: "Detailed Task Timeline",
    selectResearchers: "Select Researchers",
    inviteResearchersToCollaborate: "Invite researchers to collaborate on this project",
    collaboratorRole: "Collaborator (Can view and edit)",
    addNewTaskToTrack: "Add a new task to track project progress",
    taskDescriptionPlaceholder: "Task description...",
    selectDocument: "Select Document",
    noFileChosen: "No file chosen",
    chooseFile: "Choose File",
    uploadWordExcelPowerPoint: "Upload Word (.docx), Excel (.xlsx), or PowerPoint (.pptx) documents for collaborative editing",
    selectFilesToUpload: "Select files to upload",
    dragDropFiles: "Drag and drop files here or click to browse",
    canManageTeam: "Can manage team",
    canOnlyView: "Can only view",
    selected: "Selected",
    uploading: "Uploading...",
    uploadResearchFiles: "Upload research files to share with your team (Max 100MB per file)",
    selectFiles: "Select Files",
    descriptionOptional: "Description (Optional)",
    briefDescriptionFiles: "Brief description of the files...",
    wordDocumentPreview: "Word Document Preview",
    previewMode: "Preview mode - Download to view full content and edit in Microsoft Word or compatible applications",
    downloadToView: "Download to View",
    collaborativeEditingComingSoon: "Collaborative editing coming soon",
    previewModeDownloadToEdit: "Preview mode - Download to edit in desktop applications",
    joined: "Joined",
    priorityLabel: "Priority",
    projectFiles: "Project Files",
    upload: "Upload",
    versions: "Versions",
    owner: "Owner",
    collaborator: "Collaborator",
    save: "Save",
    
    // Status translations for dynamic values
    todo: "To Do",
    "in progress": "In Progress",
    completed: "Completed",
    high: "High",
    medium: "Medium",
    low: "Low",
    
    // File preview and sharing
    comments: "Comments",
    viewing: "viewing",
    ready: "Ready",
    shareFile: "Share File",
    shareWithOtherResearchers: "Share with other researchers",
    shareLink: "Share Link",
    shareWithResearchers: "Share with Researchers",
    messageOptional: "Message (Optional)",
    shareWith: "Share with",
    researchers: "researchers",
    
    // Research Library
    researchLibrary: "Research Library",  // Deprecated - use researcherLibrary
    researcherLibrary: "Researcher Library",
    uploadPDF: "Upload PDF",
    searchPapers: "Search papers...",
    noDocumentsFound: "No documents found",
    selectDocumentToView: "Select a document to view",
    invalidFile: "Invalid File",
    pleaseSelectPDF: "Please select a PDF file",
    uploadSuccessful: "Upload Successful",
    pdfProcessed: "PDF processed and metadata extracted",
    uploadFailed: "Upload Failed",
    failedToUploadPDF: "Failed to upload PDF",
    networkError: "Network error",
    summaryGenerated: "Summary Generated",
    aiSummaryExtracted: "AI summary and keywords extracted",
    summarizationFailed: "Summarization Failed",
    pleaseTryAgain: "Please try again",
    chatFailed: "Chat Failed",
    citationGenerated: "Citation Generated",
    citationFailed: "Citation Failed",
    documentDeleted: "Document Deleted",
    deleteFailed: "Delete Failed",
    sharingUpdated: "Sharing Updated",
    sharingFailed: "Sharing Failed",
    couldNotUpdateSharing: "Could not update sharing settings",
    failedToUpdateSharing: "Failed to update sharing settings",
    invalidTitle: "Invalid Title",
    titleCannotBeEmpty: "Title cannot be empty",
    documentRenamed: "Document Renamed",
    renameFailed: "Rename Failed",
    failedToOpenDocument: "Failed to open document",
    copied: "Copied!",
    
    // Library predefined questions
    generateSummary: "Generate summary of this paper",
    resultsOfPaper: "Results of the paper",
    conclusionsFromPaper: "Conclusions from the paper",
    explainAbstract: "Explain Abstract of this paper",
    contributionsOfPaper: "What are the contributions of this paper",
    findRelatedPapers: "Find Related Papers",
    explainPracticalImplications: "Explain the practical implications of this paper",
    summariseIntroduction: "Summarise introduction of this paper",
    literatureSurvey: "Literature survey of this paper",
    methodsUsed: "Methods used in this paper",
    dataUsedInPaper: "What data has been used in this paper",
    limitationsOfPaper: "Limitations of this paper",
    futureWorks: "Future works suggested in this paper",
    
    // Library views
    tldr: "TL;DR",
    content: "Content",
    chat: "Chat",
    citation: "Citation",
    askAboutPaper: "Ask about this paper...",
    shareDocument: "Share document",
    pdfViewer: "PDF Viewer",
    generating: "Generating...",
    citationStyle: "Citation Style",
    copyCitation: "Copy Citation",
    privateDocument: "Private Document",
    onlyYouCanView: "Only you can view this document",
    publicDocument: "Public Document",
    anyoneWithLink: "Anyone with the link can view",
    sharedWithTeam: "Shared with Team",
    projectCollaborators: "Project collaborators can view",
    researcherLibrary: "Researcher Library",
    home: "Home",
    quickQuestions: "Quick Questions",
    showMoreQuestions: "Show {count} more questions",
    selectQuestionOrType: "Select a question above or type your own",
    files: "Files",
    myDocuments: "My Documents",
    sharedWithMe: "Shared with Me",
    sharedDocuments: "Shared Documents",
    noSharedDocumentsFound: "No shared documents found",
    generateFromCrossref: "Generate from Crossref",
    generateAISummary: "Generate AI summary",
    generateSummary: "Generate Summary",
    
    // Researcher Profiles & Research Assistants
    researcherProfiles: "Researcher Profiles",
    researchAssistants: "Research Assistants",
    searchResearchers: "Search researchers...",
    searchAssistants: "Search assistants...",
    noProfilesFound: "No profiles found",
    createYourProfile: "Create your profile to connect with other researchers",
    createProfile: "Create Profile",
    editProfile: "Edit Profile",
    sendConnectionRequest: "Send Connection Request",
    connectionRequested: "Connection Requested",
    connected: "Connected",
    joinedVideoConference: "Successfully joined the video conference",
    messageOptional: "Message (Optional)",
    introduceYourself: "Introduce yourself...",
    sendRequest: "Send Request",
    researchInterestsSkills: "Research Interests / Skills",
    researchField: "Research Field",
    describeResearchField: "Describe your research field...",
    userType: "User Type",
    selectUserType: "Select user type",
    researcher: "Researcher",
    researchAssistant: "Research Assistant",
    both: "Both",
    googleScholarURL: "Google Scholar URL",
    orcidID: "ORCID ID",
    viewGoogleScholar: "View Google Scholar",
    viewORCID: "View ORCID",
    
    // Profile Cards and Labels
    researchInterests: "Research Interests",
    email: "Email",
    phone: "Phone",
    location: "Location",
    loadingProfiles: "Loading researcher profiles...",
    loadingAssistants: "Loading research assistants...",
    noResearchersFound: "No researchers found",
    noAssistantsFound: "No research assistants found",
    adjustSearchCriteria: "Try adjusting your search criteria",
    createProfileToConnect: "Create your profile to connect with other researchers",
    university: "University",
    academicLevel: "Academic Level",
    sendMessage: "Send Message",
    message: "Message",
    viewProfile: "View Profile",
    connectionPending: "Connection Pending",
    connect: "Connect",
    activeResearchers: "Active Researchers",
    activeAssistants: "Active Research Assistants",
    countriesRepresented: "Countries Represented",
    researchFields: "Research Fields",
    researcherProfilesDesc: "Connect with researchers worldwide. Browse profiles, find collaborators, and build your network.",
    researchAssistantsDesc: "Connect with qualified research assistants. Browse profiles, find support for your projects, and build your team.",
    editProfileBtn: "Edit Profile",
    
    // Connection Dialog
    sendConnectionRequestTo: "Send Connection Request to",
    connectWith: "Connect with",
    sendConnectionRequestMessage: "Send a connection request to start collaborating",
    connectMessagePlaceholder: "Hi, I'd like to connect with you to discuss potential collaboration...",
    optional: "Optional",
    
    // Edit Profile Dialog  
    editYourProfile: "Edit Your Profile",
    updateProfileInformation: "Update your researcher profile information",
    profilePhoto: "Profile Photo",
    updateProfile: "Update Profile",
    name: "Name",
    country: "Country",
    city: "City",
    selectCountry: "Select country",
    saveProfile: "Save Profile",
    saving: "Saving...",
    profileUpdated: "Profile Updated",
    profileUpdatedSuccess: "Your profile has been updated successfully",
    profileUpdateFailed: "Profile Update Failed",
    connectionRequestSent: "Connection Request Sent",
    requestSentSuccess: "Your connection request has been sent",
    requestSentTo: "Your request has been sent to",
    connectionRequestFailed: "Connection Request Failed",
    failedToUpdateProfile: "Failed to update profile",
    failedToDeleteProfile: "Failed to delete profile",
    profileDeleted: "Profile Deleted",
    profileDeletedSuccess: "Your profile has been deleted successfully",
    confirmDelete: "Are you sure you want to delete your profile? This action cannot be undone.",
    imageSizeTooLarge: "Image size should be less than 5MB",
    cannotConnect: "Cannot Connect",
    cannotConnectWithYourself: "You cannot connect with yourself",
    loginRequiredMessage: "Please login to connect with researchers",
    joinResearchCommunity: "Join Our Research Community",
    createProfileCollaborate: "Create your profile and start collaborating with researchers worldwide",
    
    expectedEnd: "Expected End",
    uploadFiles: "Upload Files",
    inviteCollaborators: "Invite Collaborators",
    createTask: "Create Task",
    
    // Agreements
    agreements: "Agreements",
    nda: "NDA",
    rca: "RCA",
    nonDisclosureAgreement: "Non-Disclosure Agreement",
    researchCollaborationAgreement: "Research Collaboration Agreement",
    signNDA: "Sign NDA",
    signRCA: "Sign RCA",
    setupRCA: "Setup RCA",
    viewAgreements: "View Agreements",
    agreementsSigned: "Agreements Signed",
    agreementContent: "Agreement Content",
    iAgree: "I Agree",
    iHaveReadAndAgree: "I have read and agree to the terms above",
    signAgreement: "Sign Agreement",
    downloadPDF: "Download PDF",
    signedBy: "Signed by",
    signedOn: "Signed on",
    digitalFingerprint: "Digital Fingerprint",
    notSigned: "Not Signed",
    projectOwner: "Project Owner",
    collaborator: "Collaborator",
    fillRCADetails: "Fill RCA Details",
    projectTitle: "Project Title",
    researchObjectives: "Research Objectives",
    expectedContributions: "Expected Contributions",
    mainResearcherAndCollaborators: "Main Researcher and Collaborators",
    saveRCATemplate: "Save RCA Template",
    rcaTemplateRequired: "The project owner must fill the RCA template before collaborators can sign",
    bothPartiesMustSign: "Both owner and collaborators must sign",
    agreementAlreadySigned: "You have already signed this agreement",
    note: "Note",
    
    // Admin Panel
    adminDashboard: "Admin Dashboard",
    welcomeBack: "Welcome back",
    backToSite: "Back to Site",
    users: "Users",
    subscriptions: "Subscriptions",
    reports: "Reports",
    totalUsers: "Total Users",
    activeSubscriptions: "Active Subscriptions",
    monthlyRevenue: "Monthly Revenue",
    totalProjects: "Total Projects",
    quickActions: "Quick Actions",
    manageUsers: "Manage Users",
    manageSubscriptions: "Manage Subscriptions",
    viewReports: "View Reports",
    platformStatistics: "Platform Statistics",
    totalResearchers: "Total Researchers",
    totalDocuments: "Total Documents",
    adminUsers: "Admin Users",
    userManagement: "User Management",
    searchByName: "Search by name or email...",
    search: "Search",
    allUsers: "All Users",
    role: "Role",
    status: "Status",
    created: "Created",
    actions: "Actions",
    active: "Active",
    inactive: "Inactive",
    activate: "Activate",
    deactivate: "Deactivate",
    deleteUser: "Delete User",
    areYouSure: "Are you sure?",
    deleteUserConfirm: "This will permanently delete the user and all their data. This action cannot be undone.",
    cancel: "Cancel",
    subscriptionManagement: "Subscription Management",
    addSubscription: "Add Subscription",
    allSubscriptions: "All Subscriptions",
    plan: "Plan",
    type: "Type",
    price: "Price",
    endDate: "End Date",
    user: "User",
    addNewSubscription: "Add New Subscription",
    selectUser: "Select a user",
    billingType: "Billing Type",
    startDate: "Start Date",
    createSubscription: "Create Subscription",
    reportsAnalytics: "Reports & Analytics",
    userGrowth: "User Growth",
    revenueAnalytics: "Revenue Analytics",
    totalRevenue: "Total Revenue",
    revenueByPlan: "Revenue by Plan",
    contentStatistics: "Content Statistics",
    platformHealth: "Platform Health",
    avgProjectsPerUser: "Average Projects per User",
    avgDocumentsPerUser: "Average Documents per User",
    subscriptionRate: "Subscription Rate",
    recentSignups: "Recent Signups",
    
    // Notification Messages
    welcome: "Welcome",
    loginSuccessful: "Login Successful",
    loginFailed: "Login Failed",
    signupFailed: "Signup Failed",
    profileUpdateSuccess: "Profile Updated Successfully",
    profileUpdateFailed: "Profile Update Failed",
    paymentSuccess: "Payment Successful",
    paymentFailed: "Payment Failed",
    subscriptionActivated: "Subscription Activated",
    accessDenied: "Access Denied",
    adminPrivilegesRequired: "Admin privileges required",
    userDeleted: "User Deleted Successfully",
    subscriptionCreated: "Subscription Created Successfully",
    errorOccurred: "An Error Occurred",
    
    // Team Matching
    findYourResearchTeam: "Find Your Research Team",
    teamMatchingDescription: "AI-powered matching to connect you with researchers sharing your interests",
    recommendedResearchTeams: "Recommended Research Teams",
    recommendedResearchers: "Recommended Researchers",
    suggestedResearchTopics: "Suggested Research Topics",
    teamMembers: "Team Members",
    teamStrengths: "Team Strengths",
    connectWith: "Connect with",
    sendConnectionRequestMessage: "Send a connection request to start collaborating",
    connectMessagePlaceholder: "Hi, I found your profile through team matching and would like to connect for potential research collaboration...",
    teamMatchingConnectDefaultMessage: "I found your profile through team matching and would like to connect for potential research collaboration.",
    noMatchesFound: "No matches found",
    beFirstToJoin: "Be one of the first! More researchers are joining the matching system.",
    adjustFilters: "Try adjusting your filters or check back later as more researchers join.",
    researchersActiveInMatching: "researchers currently active in matching",
    inviteColleagues: "Invite colleagues to join and expand your network!",
    match: "Match",
    field: "Field",
    interests: "Interests",
    optIn: "Opt In",
    optOut: "Opt Out",
    sending: "Sending...",
    
    // Dashboard Team Matching Tab
    yourMatches: "Your Matches",
    teamSuggestions: "Team Suggestions",
    status: "Status",
    active: "Active",
    recommendedTeams: "Recommended Teams",
    team: "Team",
    members: "members",
    topResearcherMatches: "Top Researcher Matches",
    viewAllMatchesAndTeams: "View All Matches & Teams",
    noMatchesFoundYet: "No matches found yet",
    findMatches: "Find Matches",
    findTeam: "Find Team",
    
    // Team Strengths (from backend)
    diverseAcademicPerspectives: "Diverse academic perspectives",
    interdisciplinaryExpertise: "Interdisciplinary expertise",
    focusedDomainKnowledge: "Focused domain knowledge",
    strongResearchAlignment: "Strong research alignment",
    internationalCollaboration: "International collaboration",
    complementarySkills: "Complementary skills",
    
    // Match Reasons (from backend)
    strongResearchInterestAlignment: "Strong research interest alignment",
    goodResearchInterestMatch: "Good research interest match",
    sameField: "Same field",
    localCollaborationOpportunity: "Local collaboration opportunity",
    complementaryExpertiseLevels: "Complementary expertise levels",
    similarResearchProfile: "Similar research profile",
    prefersCollaboration: "Prefers collaboration",
    
    // Team Matching Page
    findYourResearchTeamTitle: "Find Your Research Team",
    aiPoweredMatchingDescription: "AI-powered matching to connect you with researchers sharing your interests",
    optedIn: "Opted In",
    notOptedIn: "Not Opted In",
    activeInMatching: "Active in Matching",
    totalResearchers: "Total Researchers",
    refineYourSearch: "Refine Your Search",
    anyLanguage: "Any Language",
    anyType: "Any Type",
    allCountries: "All Countries",
    allLevels: "All Levels",
    allFields: "All Fields",
    clearFilters: "Clear Filters",
    searchMatches: "Search Matches",
    recommendedResearchTeamsCount: "Recommended Research Teams",
    teamMatchingSettings: "Team Matching Settings",
    controlVisibility: "Control your visibility in the team matching system",
    privacyNote: "Your profile is only visible to other opted-in researchers.",
    canOptOutAnytime: "You can opt out at any time.",
    currentStatus: "Current Status",
    researchersWithSynergy: "researchers with",
    synergy: "synergy",
    joinMatching: "Join Matching",
    optInDescription: "Opt in to discover researchers with similar interests and find your ideal research collaborators. Your profile visibility is controlled by you.",
    privacy: "Privacy",
    academicLevel: "Academic Level",
    collaboration: "Collaboration",
    local: "Local",
    internationalLabel: "International",
    languageFilter: "Language",
    
    // Landing Page Team Card
    semanticInterestMatching: "Semantic interest matching",
    complementaryExpertise: "Complementary expertise",
    smartRecommendations: "Smart recommendations",
    privacyControlledVisibility: "Privacy-controlled visibility",
    discoverMatches: "Discover Matches",
    
    // Notifications
    newMessage: "New Message",
    connectionAccepted: "Connection Accepted",
    connectionRequest: "Connection Request",
    newAnnouncement: "New Announcement",
    acceptedYourConnectionRequest: "accepted your connection request",
    sentYouConnectionRequest: "sent you a connection request",
    new: "New",
    
    // Team Matching - Roles and Actions
    leadResearcher: "Lead Researcher",
    coResearcher: "Co-Researcher",
    collaborator: "Collaborator",
    connectWithTeam: "Connect with Team",
    teamContact: "Team Contact",
    contactTeamMembersIndividually: "Contact team members individually through their profiles",
  },
  ar: {
    // Header
    platformTitle: "منصة التعاون البحثي",
    about: "حول",
    dashboard: "لوحة التحكم",
    services: "الخدمات",
    createProfile: "إنشاء ملف شخصي",
    contact: "اتصل بنا",
    contactSupport: "اتصل بالدعم",
    login: "تسجيل الدخول / التسجيل",
    logout: "تسجيل الخروج",
    welcome: "مرحباً",
    
    // Statistics
    collaborations: "التعاونات",
    visitors: "الزوار",
    researchProjects: "المشاريع البحثية",
    registeredUsers: "المستخدمون المسجلون",
    
    // Hero Section
    heroTitle: "معاً نحو أبحاث",
    heroTitleAccent: " أكثر عمقًا وتأثيراً",
    heroDescription: "انضم إلى المنصة الرائدة التي تربط الباحثين في جميع أنحاء العالم. شارك البيانات، تعاون في الوقت الفعلي، وانشر النتائج الرائدة معاً. قدّم بحثك باستخدام أدوات التعاون الذكية — من إدارة المشاريع إلى اكتشاف الأوراق بمساعدة الذكاء الاصطناعي. تمكين فرق البحث من خلال التواصل السلس والمشاركة الآمنة للبيانات — كل ذلك في مركز رقمي ديناميكي واحد.",
    getStarted: "ابدأ مجاناً",
    
    // Dashboard Section
    dashboardTitle: "لوحة تحكم بحثية قوية",
    dashboardDescription: "كل ما تحتاجه لإدارة مشاريعك البحثية، والتعاون مع الزملاء، وتتبع التقدم في منصة موحدة.",
    
    // Dashboard Features
    dataManagement: "إدارة البيانات",
    dataManagementDesc: "تخزين وتنظيم ومشاركة بيانات البحث بشكل آمن مع نظام التحكم في الإصدارات المتقدم والنسخ الاحتياطي.",
    teamCollaboration: "التعاون الجماعي",
    teamCollaborationDesc: "اعمل بسلاسة مع الباحثين في جميع أنحاء العالم من خلال أدوات التحرير والتواصل في الوقت الفعلي.",
    analyticsInsights: "التحليلات والرؤى",
    analyticsInsightsDesc: "تصور بياناتك باستخدام أدوات تحليلية قوية وإنشاء رؤى بحثية شاملة.",
    documentManagement: "إدارة المستندات",
    documentManagementDesc: "إنشاء وتحرير وإدارة الأوراق البحثية مع أدوات الاستشهاد المدمجة وقوالب التنسيق.",
    progressTracking: "تتبع التقدم",
    progressTrackingDesc: "راقب معالم المشروع والمواعيد النهائية ومساهمات الفريق باستخدام لوحات معلومات بديهية.",
    securityPrivacy: "الأمان والخصوصية",
    securityPrivacyDesc: "أمان على مستوى المؤسسات مع تخزين بيانات مشفر وضوابط وصول قابلة للتخصيص.",
    
    // Services Section
    servicesTitle: "خدماتنا",
    servicesDescription: "حلول شاملة مصممة لدعم كل مرحلة من رحلتك البحثية.",
    researchProjectManagement: "إدارة المشاريع البحثية",
    researchProjectManagementDesc: "إدارة دورة حياة المشروع الكاملة من الفرضية إلى النشر. تتبع التقدم، وإدارة المتعاونين، وتنظيم جميع موادك البحثية في مكان واحد.",
    globalCollaboration: "شبكة التعاون العالمية",
    globalCollaborationDesc: "تواصل مع الباحثين في جميع أنحاء العالم، وشارك النتائج، وابني شراكات تعاونية. تسهل منصتنا التعاون البحثي الدولي السلس.",
    findResearchPartner: "ابحث عن شريك البحث الخاص بك",
    findResearchPartnerDesc: "اكتشف وتواصل مع الباحثين الذين يشاركونك اهتماماتك وخبرتك. يساعدك نظام المطابقة الذكي لدينا في العثور على المتعاونين المثاليين لمشاريعك.",
    virtualDiscussionRooms: "غرف النقاش الافتراضية",
    virtualDiscussionRoomsDesc: "استضف أو انضم إلى اجتماعات افتراضية مباشرة مع مؤتمرات الفيديو ومشاركة الشاشة واللوحات البيضاء التعاونية. مثالية لفرق البحث عن بُعد والشراكات الدولية.",
    findResearchAssistant: "ابحث عن مساعد بحثي",
    findResearchAssistantDesc: "تواصل مع مساعدي البحث المؤهلين لدعم مشاريعك",
    browseQualifiedAssistants: "تصفح المساعدين المؤهلين",
    viewSkillsExperience: "عرض المهارات والخبرة",
    directCollaboration: "التعاون المباشر",
    browseResearchAssistants: "تصفح مساعدي البحث",
    researchInterestsSkills: "الاهتمامات البحثية / المهارات",
    describeInterestsSkills: "صف اهتماماتك البحثية ومجالات خبرتك ومهاراتك...",
    researchField: "مجال البحث",
    describeResearchField: "صف مجال بحثك...",
    includeResearchAreas: "قم بتضمين مجالات بحثك والمنهجيات والمهارات التقنية والخبرات",
    iAm: "أنا",
    selectYourRole: "اختر دورك",
    researcher: "باحث",
    researchAssistant: "مساعد بحثي",
    both: "كلاهما",
    roleRequired: "الدور مطلوب",
    selectRoleMessage: "يرجى اختيار دورك (باحث، مساعد بحثي، أو كلاهما)",
    
    // Service Features
    projectTimeline: "تخطيط الجدول الزمني للمشروع",
    resourceAllocation: "تخصيص الموارد",
    budgetTracking: "تتبع الميزانية",
    researcherDirectory: "دليل الباحثين",
    realtimeCollab: "أدوات التعاون في الوقت الفعلي",
    secureDataSharing: "مشاركة البيانات الآمنة",
    smartMatching: "خوارزمية مطابقة ذكية",
    browseInterests: "تصفح حسب الاهتمامات البحثية",
    directMessaging: "المراسلة المباشرة والدعوات",
    hdVideo: "مؤتمرات فيديو وصوت عالية الدقة",
    screenSharing: "مشاركة الشاشة والتسجيل",
    interactiveWhiteboard: "لوحة بيضاء تفاعلية",
    
    // Additional Service Features
    fileSharingCollab: "مشاركة الملفات والتعاون",
    onlineDocEditing: "تحرير المستندات عبر الإنترنت",
    accessTimeTracking: "تتبع وقت الوصول",
    manageYourProjects: "إدارة مشاريعك",
    researcherLibraryDesc: "قم برفع وتنظيم وتحليل أوراقك البحثية باستخدام الذكاء الاصطناعي. احصل على ملخصات فورية، وإنشاء الاستشهادات، وإدارة مكتبتك الأكاديمية بكفاءة.",
    pdfUploadManagement: "رفع وإدارة ملفات PDF",
    aiPoweredSummarization: "تلخيص مدعوم بالذكاء الاصطناعي",
    citationGeneration: "إنشاء الاستشهادات (APA، MLA، إلخ)",
    paperAnalysisInsights: "تحليل الأوراق والرؤى",
    openLibrary: "فتح المكتبة",
    browseResearchers: "تصفح الباحثين",
    joinVirtualRooms: "انضم إلى الغرف الافتراضية",
    
    // Virtual Rooms - Jitsi Integration
    participants: "المشاركون",
    plan: "الخطة",
    remaining: "متبقي",
    unlimited: "غير محدود",
    sessionEndingSoon: "الجلسة تنتهي قريباً",
    timeWarning: "تحذير الوقت",
    minutesRemaining: "دقائق متبقية",
    sessionTimeExpired: "انتهى وقت الجلسة",
    sessionWillEndAutomatically: "ستنتهي الجلسة تلقائياً",
    connecting: "جارٍ الاتصال بغرفة الفيديو...",
    goBack: "العودة",
    sessionActive: "الجلسة نشطة",
    sessionInactive: "الجلسة غير نشطة",
    privateRoom: "غرفة خاصة",
    leaveRoom: "مغادرة الغرفة",
    
    // Virtual Rooms Page
    virtualRoomsPageTitle: "غرف النقاش الافتراضية",
    virtualRoomsPageDesc: "تواصل مع الباحثين في جميع أنحاء العالم من خلال مؤتمرات الفيديو عالية الدقة. استضف أو انضم إلى مناقشات مباشرة، وشارك الشاشات، وتعاون في الوقت الفعلي.",
    allTimesInAST: "جميع الأوقات معروضة بتوقيت شبه الجزيرة العربية (AST / UTC+3)",
    createNewRoom: "إنشاء غرفة جديدة",
    mySchedule: "جدولي",
    createVirtualRoom: "إنشاء غرفة نقاش افتراضية",
    setupVideoConference: "إعداد مؤتمر فيديو جديد لفريق البحث الخاص بك",
    timesInAST: "الأوقات بتوقيت شبه الجزيرة العربية (AST / UTC+3)",
    roomType: "نوع الغرفة",
    public: "عامة",
    anyoneCanJoin: "يمكن لأي شخص الانضمام",
    private: "خاصة",
    inviteOnly: "بالدعوة فقط",
    selectParticipants: "اختر المشاركين",
    selectOneOrMore: "اختر باحثاً واحداً أو أكثر لدعوتهم إلى هذه الغرفة الخاصة",
    roomTitle: "عنوان الغرفة",
    roomTitlePlaceholder: "مثال: مناقشة بحث الذكاء الاصطناعي الأسبوعية",
    description: "الوصف",
    descriptionPlaceholder: "وصف موجز للاجتماع",
    date: "التاريخ",
    time: "الوقت",
    durationMinutes: "المدة (بالدقائق)",
    createRoom: "إنشاء الغرفة",
    hdVideo: "فيديو عالي الدقة",
    crystalClearVideo: "جودة فيديو واضحة تماماً",
    screenShare: "مشاركة الشاشة",
    presentYourResearch: "قدم بحثك",
    liveChat: "محادثة مباشرة",
    realtimeMessaging: "مراسلة في الوقت الفعلي",
    upTo50Users: "حتى 50 مستخدم",
    largeTeamMeetings: "اجتماعات فريق كبيرة",
    liveRooms: "الغرف المباشرة",
    loadingRooms: "جارٍ تحميل الغرف...",
    noLiveRooms: "لا توجد غرف مباشرة في الوقت الحالي",
    createRoomToCollaborate: "أنشئ غرفة لبدء التعاون!",
    live: "مباشر",
    host: "المضيف",
    minutes: "دقيقة",
    joinNow: "انضم الآن",
    joining: "جارٍ الانضمام...",
    privateRoom: "غرفة خاصة",
    upcomingSessions: "الجلسات القادمة",
    noUpcomingSessions: "لا توجد جلسات قادمة مجدولة",
    beFirstToCreate: "كن أول من ينشئ واحدة!",
    register: "تسجيل",
    startCollaboratingToday: "ابدأ التعاون اليوم",
    createOrJoinRooms: "أنشئ غرفة نقاش خاصة بك أو انضم إلى جلسات موجودة للتواصل مع الباحثين في جميع أنحاء العالم",
    findCollaborators: "ابحث عن متعاونين",
    roomCreatedSuccess: "تم إنشاء الغرفة بنجاح",
    roomReadyMessage: "غرفة النقاش {type} الخاصة بك جاهزة!",
    joiningRoom: "الانضمام للغرفة",
    connectingTo: "الاتصال بـ \"{title}\"...",
    accessDenied: "تم رفض الوصول",
    privateRoomNotInvited: "هذه غرفة خاصة ولم تتم دعوتك",
    pleaseCreateProfile: "يرجى إنشاء ملف شخصي كباحث أولاً",
    selectAtLeastOne: "يرجى اختيار مشارك واحد على الأقل للغرفة الخاصة",
    failedToCreateRoom: "فشل إنشاء الغرفة. يرجى المحاولة مرة أخرى.",
    failedToJoinRoom: "فشل الانضمام إلى الغرفة",
    virtualRoomsLoginRequired: "يرجى تسجيل الدخول للوصول إلى غرف النقاش الافتراضية",
    participantsSelected: "مشارك محدد",
    creating: "جاري الإنشاء...",
    roomDeletedSuccessfully: "تم حذف الغرفة بنجاح",
    roomUpdatedSuccessfully: "تم تحديث الغرفة بنجاح",
    failedToUpdateRoom: "فشل في تحديث الغرفة",
    editRoom: "تعديل الغرفة",
    editRoomDescription: "تحديث إعدادات الغرفة",
    saveChanges: "حفظ التغييرات",
    failedToDeleteRoom: "فشل في حذف الغرفة", 
    confirmDeleteRoom: "هل أنت متأكد من حذف الغرفة",
    
    // Profile Section
    profileTitle: "أنشئ ملفك الشخصي كباحث",
    profileDescription: "انضم إلى مجتمعنا العالمي من الباحثين. أنشئ ملفك الشخصي للبدء في التعاون ومشاركة الأبحاث والعثور على شركاء.",
    buildProfile: "بناء ملفك المهني",
    buildProfileDesc: "شارك اهتماماتك البحثية وتواصل مع الزملاء في جميع أنحاء العالم",
    fullName: "الاسم الكامل",
    email: "البريد الإلكتروني",
    country: "البلد",
    city: "المدينة",
    university: "الجامعة / المؤسسة",
    phoneNumber: "رقم الهاتف",
    researchInterests: "الاهتمامات البحثية",
    researchInterestsPlaceholder: "صف مجالات بحثك وخبرتك واهتماماتك... (مثل التعلم الآلي، الحوسبة الكمية، التكنولوجيا الحيوية)",
    researchInterestsHelper: "يساعد هذا الباحثين الآخرين في العثور عليك والتواصل معك",
    createProfileBtn: "إنشاء الملف الشخصي",
    viewAllProfiles: "عرض جميع الملفات",
    
    // Profile Form - Additional Labels
    signInRequired: "يتطلب تسجيل الدخول",
    signInToCreateProfile: "يرجى تسجيل الدخول لإنشاء ملفك الشخصي كباحث والانضمام إلى مجتمعنا",
    signInToCreateProfileBtn: "تسجيل الدخول لإنشاء الملف الشخصي",
    profilePhotoOptional: "صورة الملف الشخصي (اختياري)",
    uploadPhotoHelper: "قم برفع صورة احترافية (حد أقصى 5 ميجابايت، JPG/PNG) - اختياري",
    fullNameRequired: "الاسم الكامل *",
    emailRequired: "البريد الإلكتروني *",
    countryRequired: "البلد *",
    cityRequired: "المدينة *",
    universityRequired: "الجامعة / المؤسسة *",
    phoneOptional: "رقم الهاتف",
    researchInterestsRequired: "الاهتمامات البحثية *",
    googleScholarOptional: "رابط Google Scholar (اختياري)",
    googleScholarPlaceholder: "https://scholar.google.com/citations?user=...",
    googleScholarHelper: "رابط إلى ملفك الشخصي على Google Scholar",
    orcidOptional: "معرف ORCID (اختياري)",
    orcidPlaceholder: "0000-0000-0000-0000",
    orcidHelper: "معرفك الفريد كباحث",
    createProfileDesc: "انضم إلى مجتمعنا العالمي من الباحثين واعثر على المتعاونين المثاليين",
    
    // Profile Form Placeholders
    namePlaceholder: "د. جين سميث",
    emailPlaceholder: "jane.smith@university.edu",
    countryPlaceholder: "الولايات المتحدة",
    cityPlaceholder: "بوسطن",
    universityPlaceholder: "معهد ماساتشوستس للتكنولوجيا",
    phonePlaceholder: "+1 (555) 123-4567",
    interestsPlaceholder: "صف مجالات بحثك وخبرتك واهتماماتك... (مثل التعلم الآلي، الحوسبة الكمية، التكنولوجيا الحيوية)",
    interestsHelper: "يساعد هذا الباحثين الآخرين في العثور عليك والتواصل معك",
    
    // Profile Benefits
    getDiscovered: "اجعل نفسك مكتشفاً",
    getDiscoveredDesc: "اجعل من السهل على الباحثين العثور عليك بناءً على خبرتك واهتماماتك",
    buildNetwork: "بناء الشبكة",
    buildNetworkDesc: "تواصل مع الزملاء والمتعاونين والشركاء البحثيين المحتملين في جميع أنحاء العالم",
    growImpact: "زيادة التأثير",
    growImpactDesc: "زد من ظهور بحثك وتعاون في مشاريع رائدة",
    
    // Contact Section
    getInTouch: "تواصل معنا",
    getInTouchDesc: "هل لديك أسئلة حول منصتنا؟ فريقنا هنا لمساعدتك على البدء في رحلة التعاون البحثي الخاصة بك.",
    emailLabel: "البريد الإلكتروني",
    whatsappLabel: "واتساب",
    sendMessage: "أرسل لنا رسالة",
    sendMessageDesc: "سنرد خلال 24 ساعة",
    nameLabel: "الاسم",
    messageLabel: "الرسالة",
    messagePlaceholder: "أخبرنا عن احتياجاتك البحثية...",
    sendBtn: "إرسال الرسالة",
    
    // Footer
    footerTagline: "تمكين الباحثين من التعاون والابتكار من أجل غد أفضل.",
    platform: "المنصة",
    resources: "الموارد",
    legal: "القانونية",
    documentation: "التوثيق",
    apiReference: "مرجع API",
    support: "الدعم",
    blog: "المدونة",
    pricing: "التسعير",
    privacyPolicy: "سياسة الخصوصية",
    termsOfService: "شروط الخدمة",
    cookiePolicy: "سياسة ملفات تعريف الارتباط",
    gdpr: "اللائحة العامة لحماية البيانات",
    copyright: "© 2025 RCP. جميع الحقوق محفوظة.",
    
    // Auth
    loginTitle: "تسجيل الدخول إلى RCP",
    loginDesc: "أدخل بيانات الاعتماد الخاصة بك للوصول إلى لوحة البحث الخاصة بك",
    createAccountTitle: "إنشاء حساب",
    createAccountDesc: "انضم إلى مجتمعنا البحثي اليوم",
    name: "الاسم",
    password: "كلمة المرور",
    forgotPassword: "نسيت كلمة المرور؟",
    loginBtn: "تسجيل الدخول",
    signUpBtn: "التسجيل",
    alreadyHaveAccount: "هل لديك حساب بالفعل؟ ",
    dontHaveAccount: "ليس لديك حساب؟ ",
    signUpLink: "سجل",
    loginLink: "تسجيل الدخول",
    
    // Auth Dialog - Additional
    emailPlaceholderAuth: "researcher@university.edu",
    passwordPlaceholderCreate: "أنشئ كلمة مرور قوية",
    passwordPlaceholderEnter: "أدخل كلمة المرور الخاصة بك",
    completeResearcherProfile: "أكمل ملفك الشخصي كباحث",
    completeProfileDesc: "املأ التفاصيل الخاصة بك لإنشاء ملفك الشخصي كباحث. الحقول المميزة بـ * مطلوبة.",
    universityInstitution: "الجامعة / المؤسسة *",
    phoneOptionalShort: "الهاتف (اختياري)",
    describeInterestsPlaceholder: "صف اهتماماتك البحثية ومجالات خبرتك...",
    cancel: "إلغاء",
    
    // Toast Messages
    loginSuccess: "تم تسجيل الدخول بنجاح",
    loginSuccessDesc: "مرحباً بعودتك إلى RCP!",
    accountCreated: "تم إنشاء الحساب",
    accountCreatedDesc: "مرحباً بك في RCP!",
    messageSent: "تم إرسال الرسالة",
    profileCreated: "تم إنشاء الملف الشخصي بنجاح",
    profileCreatedDesc: "إعادة التوجيه إلى ملفات الباحثين...",
    loggedOut: "تم تسجيل الخروج",
    loggedOutDesc: "تم تسجيل خروجك بنجاح.",
    
    // Pricing Section
    pricingTitle: "تسعير بسيط وشفاف",
    pricingDesc: "اختر الخطة التي تناسبك",
    monthly: "شهرياً",
    yearly: "سنوياً",
    saveUpTo: "وفر حتى",
    save20: "وفر 20%",
    perMonth: "/ شهر",
    perYear: "/ سنة",
    basic: "أساسي",
    premium: "مميز",
    advanced: "متقدم",
    mostPopular: "الأكثر شعبية",
    forIndividuals: "مثالي للبدء",
    forResearchers: "للباحثين الأفراد",
    forPowerUsers: "للمستخدمين المتقدمين",
    getStartedBtn: "ابدأ الآن",
    startPremium: "ابدأ المميز",
    subscribeNow: "اشترك الآن",
    allPlansInclude: "جميع الخطط تشمل تخزين آمن للبيانات ونسخ احتياطي منتظم",
    needCustomPlan: "تحتاج خطة مخصصة؟",
    contactUs: "اتصل بنا",
    forEnterprise: "للحلول المؤسسية",
    
    // Pricing Features
    aiCredits: "رصيد الذكاء الاصطناعي شهرياً",
    researchProjects: "مشاريع بحثية",
    storage: "مساحة تخزين",
    virtualRooms: "غرف افتراضية",
    basicVirtualRooms: "4 غرف/شهر (30 دقيقة لكل غرفة)",
    
    // Storage Display
    storageUsage: "استخدام التخزين",
    of: "من",
    left: "متبقي",
    used: "مستخدم",
    libraryDocuments: "مستندات المكتبة",
    projectFiles: "ملفات المشروع",
    basicPlan: "الخطة الأساسية",
    premiumPlan: "الخطة المميزة",
    advancedPlan: "الخطة المتقدمة",
    gb: "جيجابايت",
    mb: "ميجابايت",
    kb: "كيلوبايت",
    bytes: "بايت",
    unlimitedVirtualRooms: "غرف غير محدودة (وقت غير محدود)",
    unlimitedRoomsRecording: "غرف غير محدودة مع التسجيل",
    communitySupport: "دعم المجتمع",
    prioritySupport: "دعم بريد إلكتروني ذو أولوية",
    support247: "دعم ذو أولوية على مدار الساعة",
    basicDocManagement: "إدارة مستندات أساسية",
    fiftyAICredits: "50 رصيد ذكاء اصطناعي شهرياً",
    threeResearchProjects: "3 مشاريع بحثية",
    fiveGBStorage: "5 جيجابايت تخزين",
    basicRooms: "4 غرف/شهر (30 دقيقة لكل غرفة)",
    fiveHundredAICredits: "500 رصيد ذكاء اصطناعي شهرياً",
    fiftyGBStorage: "50 جيجابايت تخزين",
    twoThousandFiveHundredAICredits: "2,500 رصيد ذكاء اصطناعي شهرياً",
    fiveHundredGBStorage: "500 جيجابايت تخزين",
    advancedAI: "تحليل الذكاء الاصطناعي المتقدم",
    collaborationTools: "أدوات التعاون",
    exportFormats: "التصدير بصيغ متعددة",
    tenResearchProjects: "10 مشاريع بحثية",
    extendedVirtualRooms: "10 غرف/شهر (ساعتان لكل غرفة)",
    realtimeCollaboration: "التعاون في الوقت الفعلي",
    documentSharingVersioning: "مشاركة المستندات والتحكم في الإصدارات",
    unlimitedProjectsCollaborators: "مشاريع ومتعاونين غير محدودين",
    unlimitedProjects: "مشاريع بحثية غير محدودة",
    unlimitedTeamMembers: "مشاريع وأعضاء فريق غير محدودين",
    dedicatedAccountManager: "مدير حساب مخصص",
    customIntegrations: "عمليات تكامل مخصصة",
    apiAccess: "وصول API",
    advancedAnalytics: "تحليلات متقدمة",
    advancedAnalyticsDashboard: "لوحة تحليلات متقدمة",
    apiAccessIntegrations: "وصول API للتكاملات",
    customDataExport: "خيارات تصدير بيانات مخصصة",
    enhancedSecurity: "ميزات أمان محسّنة",
    dedicatedSupportChannel: "قناة دعم مخصصة",
    whiteLabelOptions: "خيارات العلامة البيضاء",
    ssoIntegration: "تسجيل الدخول الموحد (SSO)",
    
    // Pricing Additional
    saveYearly: "وفر 24$ سنوياً (90 ريال)",
    saveYearlyAdvanced: "وفر 120$ سنوياً (450 ريال)",
    allPlansIncludeStorage: "جميع الخطط تشمل تخزين آمن للبيانات ونسخ احتياطي منتظم",
    needCustomPlan: "تحتاج خطة مخصصة؟",
    contactUs: "اتصل بنا",
    forEnterpriseSolutions: "للحلول المؤسسية",
    
    // Community Forum
    communityForum: "منتدى المجتمع",
    announcements: "الإعلانات",
    announcementsDesc: "ابق على اطلاع بآخر الأخبار والأحداث والتحديثات المهمة من مجتمع البحث.",
    clickToView: "انقر للعرض",
    createAnnouncement: "إنشاء إعلان",
    createAnnouncementDesc: "شارك الأخبار والتحديثات المهمة مع مجتمع البحث",
    editAnnouncement: "تعديل الإعلان",
    deleteAnnouncement: "حذف الإعلان",
    confirmDeleteAnnouncement: "هل أنت متأكد من حذف هذا الإعلان؟ لا يمكن التراجع عن هذا الإجراء.",
    searchAnnouncements: "البحث في الإعلانات...",
    noAnnouncements: "لا توجد إعلانات بعد",
    beFirstToPost: "كن أول من ينشر إعلاناً",
    signInToView: "يرجى تسجيل الدخول لعرض الإعلانات",
    enterTitle: "أدخل عنوان الإعلان",
    enterContent: "أدخل محتوى الإعلان",
    image: "صورة",
    optional: "اختياري",
    maxImageSize: "الحد الأقصى لحجم الصورة:",
    uploading: "جاري الرفع...",
    recentAnnouncements: "أحدث الإعلانات",
    stayUpdatedWithLatest: "ابق على اطلاع بآخر الأخبار والتحديثات من مجتمعنا",
    noAnnouncementsYet: "لا توجد إعلانات حتى الآن",
    viewAllAnnouncements: "عرض جميع الإعلانات",
    announcement: "إعلان",
    new: "جديد",
    joinVibrantCommunity: "انضم إلى مجتمعنا النابض بالحياة من الباحثين. اطرح الأسئلة، شارك الأفكار، وتعاون في موضوعات البحث المتطورة",
    askAnswerQuestions: "اطرح وأجب عن الأسئلة",
    askAnswerQuestionsDesc: "انشر أسئلتك البحثية واحصل على إجابات من زملائك الباحثين في مجالك",
    shareKnowledge: "شارك المعرفة",
    shareKnowledgeDesc: "ساهم بخبرتك وساعد في تطوير البحث من خلال مشاركة الأفكار وأفضل الممارسات",
    connectWithExperts: "تواصل مع الخبراء",
    connectWithExpertsDesc: "تواصل مع الباحثين في جميع أنحاء العالم وابني تعاونات هادفة",
    engageReact: "تفاعل وأبدِ رأيك",
    engageReactDesc: "استخدم الرموز التعبيرية لإظهار التقدير وإبراز المساهمات القيمة",
    recentDiscussions: "المناقشات الأخيرة",
    joinTheDiscussion: "انضم إلى النقاش",
    views: "مشاهدة",
    answers: "إجابات",
    live: "مباشر",
    general: "عام",
    welcomeToRC: "مرحباً بك في مجتمع منصة التعاون البحثي!",
    beFirstToDiscuss: "كن أول من يبدأ مناقشة!",
    
    // Community Forum Page
    connectShareCollaborate: "تواصل، شارك المعرفة، وتعاون مع الباحثين",
    askQuestion: "اطرح سؤالاً",
    searchQuestions: "البحث في الأسئلة...",
    category: "الفئة",
    sortBy: "الترتيب حسب",
    newestFirst: "الأحدث أولاً",
    oldestFirst: "الأقدم أولاً",
    mostPopular: "الأكثر شعبية",
    unanswered: "غير مجاب عليها",
    noQuestionsFound: "لم يتم العثور على أسئلة. كن أول من يسأل!",
    loadingForum: "جاري تحميل المنتدى...",
    answered: "تم الإجابة",
    postQuestion: "نشر السؤال",
    postAnswer: "نشر الإجابة",
    yourAnswer: "إجابتك",
    shareYourKnowledge: "شارك معرفتك وساعد المجتمع...",
    answer: "إجابة",
    backToForum: "العودة إلى المنتدى",
    editPost: "تحرير المنشور",
    deletePost: "حذف المنشور",
    editAnswer: "تحرير الإجابة",
    deleteAnswer: "حذف الإجابة",
    updatePost: "تحديث المنشور",
    updateAnswer: "تحديث الإجابة",
    deleteConfirmPost: "هل أنت متأكد من حذف هذا المنشور؟ لا يمكن التراجع عن هذا الإجراء.",
    deleteConfirmAnswer: "هل أنت متأكد من حذف هذه الإجابة؟ لا يمكن التراجع عن هذا الإجراء.",
    updateYourQuestion: "حدّث سؤالك أو منشورك",
    updateYourAnswer: "حدّث إجابتك",
    title: "العنوان",
    whatsYourQuestion: "ما هو سؤالك؟",
    details: "التفاصيل",
    provideMoreDetails: "قدم المزيد من التفاصيل حول سؤالك...",
    shareKnowledgePlaceholder: "شارك معرفتك...",
    askQuestionTitle: "اطرح سؤالاً",
    shareQuestionWithCommunity: "شارك سؤالك مع المجتمع البحثي",
    fillAllFields: "يرجى ملء جميع الحقول",
    enterAnswer: "يرجى إدخال إجابة",
    postCreatedSuccess: "تم إنشاء المنشور بنجاح!",
    postUpdatedSuccess: "تم تحديث المنشور بنجاح!",
    postDeletedSuccess: "تم حذف المنشور بنجاح!",
    answerPostedSuccess: "تم نشر الإجابة بنجاح!",
    answerUpdatedSuccess: "تم تحديث الإجابة بنجاح!",
    answerDeletedSuccess: "تم حذف الإجابة بنجاح!",
    failedToCreatePost: "فشل في إنشاء المنشور",
    failedToUpdatePost: "فشل في تحديث المنشور",
    failedToDeletePost: "فشل في حذف المنشور",
    failedToPostAnswer: "فشل في نشر الإجابة",
    failedToUpdateAnswer: "فشل في تحديث الإجابة",
    failedToDeleteAnswer: "فشل في حذف الإجابة",
    researcherProfileRequired: "ملف الباحث مطلوب",
    needProfileForForum: "تحتاج إلى إنشاء ملف شخصي للباحث للوصول إلى منتدى المجتمع والمشاركة في المناقشات.",
    needProfileForLibrary: "تحتاج إلى إنشاء ملف شخصي للباحث للوصول إلى المكتبة وإدارة مستنداتك.",
    profileNotFound: "الملف الشخصي غير موجود",
    errorLoadingProfile: "خطأ في تحميل الملف الشخصي",
    unableToLoadProfile: "غير قادر على تحميل ملفك الشخصي للباحث. يرجى المحاولة مرة أخرى أو التحقق من اتصالك.",
    retry: "إعادة المحاولة",
    createProfile: "إنشاء ملف شخصي",
    goHome: "العودة للرئيسية",
    authenticationRequired: "المصادقة مطلوبة",
    signInToAccessLibrary: "يرجى تسجيل الدخول للوصول إلى المكتبة وإدارة مستندات البحث الخاصة بك.",
    createResearcherProfile: "إنشاء ملف الباحث",
    joinCommunityForum: "انضم إلى منتدى المجتمع",
    signInToAccessForum: "يرجى تسجيل الدخول للوصول إلى منتدى المجتمع والتواصل مع الباحثين.",
    signIn: "تسجيل الدخول",
    bestAnswer: "أفضل إجابة",
    like: "إعجاب",
    love: "حب",
    insightful: "ثاقب",
    helpful: "مفيد",
    thinking: "تفكير",
    
    // Forum Categories
    allTopics: "جميع المواضيع",
    generalDiscussion: "نقاش عام",
    researchMethods: "طرق البحث",
    dataAnalysis: "تحليل البيانات",
    literatureReview: "مراجعة الأدبيات",
    careerAcademia: "المهنة والأكاديمية",
    toolsSoftware: "الأدوات والبرمجيات",
    collaborationOpportunities: "فرص التعاون",
    feedbackReview: "التعليقات والمراجعة",
    
    // Virtual Rooms - Extended Features
    currentPlan: "الخطة الحالية",
    sessionLimit: "حد الجلسة",
    roomTitle: "عنوان الغرفة", 
    enterRoomTitle: "أدخل عنوان الغرفة...",
    optionalDescription: "وصف اختياري...",
    plannedDuration: "المدة المخططة",
    minutes: "دقائق",
    hour: "ساعة",
    hours: "ساعات",
    roomPrivacy: "خصوصية الغرفة",
    publicRoom: "غرفة عامة",
    anyoneCanJoin: "يمكن لأي شخص الانضمام",
    inviteOnly: "بالدعوة فقط",
    inviteParticipants: "دعوة المشاركين",
    createRoom: "إنشاء الغرفة",
    createNewRoom: "إنشاء غرفة جديدة",
    createRoomDescription: "إعداد غرفة نقاش افتراضية جديدة للتعاون",
    roomCreatedSuccessfully: "تم إنشاء الغرفة بنجاح!",
    failedToCreateRoom: "فشل إنشاء الغرفة",
    roomLimitReached: "تم الوصول إلى حد الغرف",
    monthlyRoomLimitExceeded: "لقد وصلت إلى الحد الشهري للغرف",
    roomsUsedThisMonth: "الغرف المستخدمة هذا الشهر",
    roomsThisMonth: "غرف هذا الشهر",
    monthlyRoomLimit: "الحد الشهري للغرف",
    roomsPerMonth: "غرف/شهر",
    accessDenied: "تم رفض الوصول",
    privateRoomAccessDenied: "ليس لديك إذن للانضمام إلى هذه الغرفة الخاصة",
    failedToJoinRoom: "فشل الانضمام إلى الغرفة",
    sessionEnded: "انتهت الجلسة",
    sessionEndedSuccessfully: "انتهت الجلسة بنجاح",
    liveRooms: "الغرف المباشرة",
    active: "نشط",
    live: "مباشر",
    joinNow: "انضم الآن",
    joining: "جارٍ الانضمام...",
    upcomingSessions: "الجلسات القادمة", 
    scheduled: "مجدولة",
    upcoming: "قادمة",
    startsAt: "تبدأ في",
    hostedBy: "مستضافة من قبل",
    duration: "المدة",
    noRoomsYet: "لا توجد غرف بعد",
    beFirstToCreateRoom: "كن أول من ينشئ غرفة نقاش افتراضية",
    createFirstRoom: "إنشاء أول غرفة",
    upgradeForLongerSessions: "ترقية للجلسات الأطول",
    getCurrentLimitUpgradeMessage: "الخطة الأساسية تحدد الجلسات بـ 30 دقيقة. قم بالترقية للمناقشات الأطول!",
    upgradePlan: "ترقية الخطة",
    selectAtLeastOne: "يرجى اختيار مشارك واحد على الأقل للغرف الخاصة",
    pleaseCreateProfile: "يرجى إنشاء ملف شخصي كباحث أولاً",
    profileRequired: "ملف شخصي مطلوب",
    virtualRoomsLoginRequired: "يرجى تسجيل الدخول للوصول إلى غرف النقاش الافتراضية",
    
    // Subscription Page
    subscription: "الاشتراك",
    manageSubscription: "إدارة اشتراكك وعرض تفاصيل خطتك",
    basicPlan: "الخطة الأساسية",
    premiumPlan: "الخطة المتميزة",
    advancedPlan: "الخطة المتقدمة",
    freePlan: "خطة مجانية",
    activeSubscription: "اشتراك نشط",
    cancelled: "ملغي",
    pendingPayment: "بانتظار الدفع",
    upgradePlan: "ترقية الخطة",
    changePlan: "تغيير الخطة",
    cancelSubscription: "إلغاء الاشتراك",
    cancelling: "جاري الإلغاء...",
    choosePlan: "اختر خطتك",
    selectPerfectPlan: "اختر الخطة المثالية لاحتياجاتك البحثية",
    currentPlanLabel: "الخطة الحالية",
    currentPlan: "الخطة الحالية",
    status: "الحالة",
    month: "شهر",
    nextBilling: "الفاتورة التالية",
    forIndividuals: "مثالي للبدء",
    forPowerUsers: "للمستخدمين المتقدمين",
    subscribeNow: "اشترك الآن",
    getStartedFree: "ابدأ مجاناً",
    securePaymentPayPal: "دفع آمن عبر PayPal",
    safeSecurePayment: "معالجة دفع آمنة ومحمية",
    cancelAnytime: "إلغاء في أي وقت - بدون التزام طويل الأمد",
    automaticBilling: "فوترة تلقائية في نفس التاريخ من كل شهر",
    instantAccess: "وصول فوري للميزات عند الاشتراك",
    backToHome: "العودة للرئيسية",
    processingPayment: "جاري المعالجة...",
    paymentSuccessTitle: "تم الدفع بنجاح!",
    paymentSuccessDesc: "اشتراكك نشط الآن.",
    sessionExpired: "انتهت الجلسة",
    tryAgain: "يرجى المحاولة مرة أخرى.",
    loginRequired: "يتطلب تسجيل الدخول",
    pleaseLoginToSubscribe: "يرجى تسجيل الدخول للاشتراك.",
    paymentCancelled: "تم إلغاء الدفع",
    paymentCancelledDesc: "لقد ألغيت عملية الدفع.",
    purchaseSuccessful: "تم الشراء بنجاح!",
    creditsAdded: "رصيد تمت إضافته إلى حسابك",
    paymentFailed: "فشل الدفع",
    pleaseWait: "يرجى الانتظار بينما نعالج دفعتك...",
    error: "خطأ",
    purchaseFailed: "فشل الشراء",
    
    // Library
    researcherLibrary: "مكتبة الباحث",
    aiCreditsLabel: "رصيد الذكاء الاصطناعي",
    aiCreditsLoading: "رصيد الذكاء الاصطناعي (جاري تحميل الملف الشخصي...)",
    purchaseAICredits: "شراء رصيد الذكاء الاصطناعي",
    getMoreCredits: "احصل على المزيد من الرصيد لمواصلة استخدام ميزات الذكاء الاصطناعي",
    runningLowCredits: "ينفد رصيد الذكاء الاصطناعي!",
    creditsRemainingMessage: "لديك {count} رصيد متبقي. فكر في شراء المزيد أو ترقية خطتك.",
    creditsPerMonth: "رصيد/شهر",
    credits: "رصيد",
    total: "الإجمالي",
    purchaseCredits: "شراء الرصيد",
    upgrade: "ترقية",
    free: "مجاني",
    plan: "خطة",
    buyCredits: "شراء رصيد",
    uploadPDF: "رفع ملف PDF",
    myDocuments: "مستنداتي",
    searchDocuments: "البحث في المستندات...",
    noDocuments: "لم يتم رفع أي مستندات بعد",
    uploadFirst: "قم برفع أول ملف PDF للبدء",
    summarize: "تلخيص",
    chat: "محادثة",
    keywords: "كلمات مفتاحية",
    citation: "استشهاد",
    rename: "إعادة تسمية",
    delete: "حذف",
    download: "تحميل",
    processing: "جاري المعالجة...",
    
    // Folders
    folders: "المجلدات",
    createFolder: "إنشاء مجلد",
    newFolder: "مجلد جديد",
    folderName: "اسم المجلد",
    enterFolderName: "أدخل اسم المجلد",
    createNewFolder: "إنشاء مجلد جديد",
    renameFolder: "إعادة تسمية المجلد",
    deleteFolder: "حذف المجلد",
    moveToFolder: "نقل إلى مجلد",
    moveTo: "نقل إلى",
    selectFolder: "اختر مجلداً",
    rootFolder: "الجذر (بدون مجلد)",
    folderCreated: "تم إنشاء المجلد",
    folderCreatedSuccess: "تم إنشاء المجلد بنجاح",
    folderRenamed: "تم إعادة تسمية المجلد",
    folderRenamedSuccess: "تم إعادة تسمية المجلد بنجاح",
    folderDeleted: "تم حذف المجلد",
    folderDeletedSuccess: "تم حذف المجلد بنجاح",
    folderShared: "تمت مشاركة المجلد",
    folderSharedSuccess: "تمت مشاركة المجلد بنجاح",
    documentMoved: "تم نقل المستند",
    documentMovedSuccess: "تم نقل المستند بنجاح",
    confirmDeleteFolder: "هل أنت متأكد من حذف هذا المجلد؟",
    folderNotEmpty: "يحتوي هذا المجلد على مستندات أو مجلدات فرعية",
    emptyFolderFirst: "يرجى إفراغ المجلد قبل الحذف",
    shareFolder: "مشاركة المجلد",
    sharedFolders: "المجلدات المشتركة",
    folderPermissions: "أذونات المجلد",
    viewOnly: "عرض فقط",
    canEdit: "يمكنه التحرير",
    sharedWith: "مشترك مع",
    selectResearchers: "اختر الباحثين",
    noFolders: "لا توجد مجلدات بعد",
    organizeDocs: "قم بتنظيم مستنداتك عن طريق إنشاء المجلدات",
    createRootFolderDesc: "إنشاء مجلد جديد لتنظيم مستنداتك",
    createSubfolderDesc: "إنشاء مجلد فرعي داخل المجلد المحدد",
    
    // Dashboard
    researcherDashboard: "لوحة تحكم الباحث",
    home: "الرئيسية",
    logout: "تسجيل الخروج",
    projects: "المشاريع",
    library: "المكتبة",
    notifications: "الإشعارات",
    messages: "الرسائل",
    groups: "المجموعات",
    groupMessages: "رسائل المجموعة",
    createGroup: "إنشاء مجموعة",
    groupName: "اسم المجموعة",
    enterGroupName: "أدخل اسم المجموعة",
    selectMembers: "اختر الأعضاء",
    addMembers: "إضافة أعضاء",
    groupCreated: "تم إنشاء المجموعة",
    groupCreatedSuccess: "تم إنشاء المجموعة بنجاح",
    leaveGroup: "مغادرة المجموعة",
    deleteGroup: "حذف المجموعة",
    confirmLeaveGroup: "هل أنت متأكد من مغادرة هذه المجموعة؟",
    confirmDeleteGroup: "هل أنت متأكد من حذف هذه المجموعة؟ سيتم فقدان جميع الرسائل.",
    members: "الأعضاء",
    member: "عضو",
    you: "أنت",
    creator: "المنشئ",
    sendMessage: "إرسال رسالة",
    typeMessage: "اكتب رسالة...",
    noGroups: "لا توجد مجموعات",
    noGroupsDesc: "لم تنضم إلى أي مجموعات بعد. أنشئ مجموعة لبدء التعاون!",
    groupDeleted: "تم حذف المجموعة",
    leftGroup: "غادرت المجموعة",
    memberAdded: "تمت إضافة العضو",
    memberRemoved: "تمت إزالة العضو",
    myConnections: "اتصالاتي",
    myProjects: "مشاريعي",
    noNotifications: "لا توجد إشعارات بعد",
    stayTuned: "أنت محدث بالكامل! ترقب التحديثات.",
    noMessages: "لا توجد رسائل بعد",
    selectConnection: "اختر اتصالاً لبدء المراسلة",
    sendMessage: "إرسال رسالة",
    typeMessage: "اكتب رسالتك...",
    messageSent: "تم إرسال الرسالة",
    messageDelivered: "تم تسليم رسالتك",
    error: "خطأ",
    failedToSend: "فشل في إرسال الرسالة",
    noConnections: "لا توجد اتصالات بعد",
    startConnecting: "ابدأ بالتواصل مع باحثين آخرين للتعاون",
    browseResearchers: "تصفح الباحثين",
    noProjects: "لا توجد مشاريع نشطة",
    createProject: "أنشئ مشروعك الأول للبدء",
    viewAllProjects: "عرض جميع المشاريع",
    newProject: "مشروع جديد",
    dueIn: "مستحق خلال",
    days: "أيام",
    overdue: "متأخر",
    noProfileFound: "لم يتم العثور على ملف باحث",
    createProfileFirst: "يرجى إنشاء ملف باحث أولاً",
    goToHome: "الذهاب للرئيسية",
    connectionRequests: "طلبات الاتصال",
    acceptRequest: "قبول",
    declineRequest: "رفض",
    noRequests: "لا توجد طلبات معلقة",
    requestAccepted: "تم قبول الطلب",
    connectionEstablished: "تم إنشاء الاتصال بنجاح",
    requestDeclined: "تم رفض الطلب",
    
    // Projects Page
    myProjectsTitle: "مشاريعي البحثية",
    createNewProject: "إنشاء مشروع جديد",
    searchProjects: "البحث في المشاريع...",
    noProjectsYet: "لا توجد مشاريع بعد",
    createFirstProject: "أنشئ مشروعك الأول لبدء التعاون",
    projectTitle: "عنوان المشروع",
    projectDescription: "وصف المشروع",
    startDate: "تاريخ البدء",
    endDate: "تاريخ الانتهاء",
    status: "الحالة",
    active: "نشط",
    completed: "مكتمل",
    planning: "قيد التخطيط",
    onHold: "معلق",
    cancelled: "ملغى",
    createProject: "إنشاء المشروع",
    cancel: "إلغاء",
    members: "الأعضاء",
    tasks: "المهام",
    files: "الملفات",
    progress: "التقدم",
    dueDate: "تاريخ الاستحقاق",
    overdue: "متأخر",
    projectCreated: "تم إنشاء المشروع",
    projectCreatedSuccess: "تم إنشاء مشروعك بنجاح",
    projectDeleted: "تم حذف المشروع",
    projectDeletedSuccess: "تم حذف المشروع",
    confirmDelete: "هل أنت متأكد من حذف هذا المشروع؟",
    deleteProject: "حذف المشروع",
    editProject: "تعديل المشروع",
    editProjectDescription: "قم بتحديث معلومات مشروعك",
    updateProject: "تحديث المشروع",
    projectUpdated: "تم تحديث المشروع",
    projectUpdatedMessage: "تم تحديث المشروع بنجاح.",
    updateFailed: "فشل التحديث",
    failedToUpdateProject: "فشل تحديث المشروع. يرجى المحاولة مرة أخرى.",
    viewDetails: "عرض التفاصيل",
    activeProjects: "المشاريع النشطة",
    totalProjects: "إجمالي المشاريع",
    currentlyInProgress: "قيد التنفيذ حالياً",
    successfullyFinished: "تم الانتهاء بنجاح",
    projectsYouCollaborate: "المشاريع التي تتعاون فيها",
    loading: "جارٍ التحميل",
    of: "من",
    
    // Project Detail Page
    overview: "نظرة عامة",
    timeline: "الجدول الزمني",
    documents: "المستندات",
    addTask: "إضافة مهمة",
    editTask: "تعديل المهمة",
    taskTitle: "عنوان المهمة",
    taskDescription: "وصف المهمة",
    assignee: "المكلف",
    assignTo: "تعيين إلى",
    priority: "الأولوية",
    high: "عالية",
    medium: "متوسطة",
    low: "منخفضة",
    addMilestone: "إضافة معلم",
    milestone: "معلم",
    uploadDocument: "رفع مستند",
    documentName: "اسم المستند",
    uploadedBy: "رفع بواسطة",
    inviteMember: "دعوة عضو",
    removeMember: "إزالة عضو",
    role: "الدور",
    permissions: "الصلاحيات",
    owner: "مالك",
    editor: "محرر",
    viewer: "مشاهد",
    recentActivity: "النشاط الأخير",
    comments: "التعليقات",
    addComment: "إضافة تعليق",
    writeComment: "اكتب تعليقاً...",
    projectInformation: "معلومات المشروع",
    researchArea: "مجال البحث",
    collaborators: "المتعاونون",
    completionRate: "معدل الإنجاز",
    tasksCompleted: "المهام المكتملة",
    allYourProjects: "جميع مشاريعك",
    projectsYouJoined: "المشاريع التي انضممت إليها",
    collaboratorCount: "متعاون(ون)",
    fileCount: "ملف(ات)",
    lastAccess: "آخر دخول",
    you: "أنت",
    projectTitlePlaceholder: "على سبيل المثال، دراسة الكشف عن السرطان بالذكاء الاصطناعي",
    loginRequiredMessage: "يرجى تسجيل الدخول للوصول إلى المشاريع البحثية",
    confirmDeleteMessage: "هل أنت متأكد من حذف هذا المشروع؟ لا يمكن التراجع عن هذا الإجراء.",
    projectDeletedMessage: "تم حذف المشروع نهائياً.",
    deleteFailed: "فشل الحذف",
    errorTitle: "خطأ",
    createProfileFirst: "يرجى إنشاء ملف تعريف الباحث أولاً",
    projectCreatedSuccessTitle: "تم إنشاء المشروع بنجاح",
    projectReadyMessage: "مشروعك البحثي جاهز!",
    failedToCreateProject: "فشل إنشاء المشروع. يرجى المحاولة مرة أخرى.",
    
    aiSummary: "ملخص الذكاء الاصطناعي",
    chatWithDocument: "محادثة مع المستند",
    askQuestion: "اسأل سؤالاً حول هذا المستند...",
    predefinedQuestions: "أسئلة محددة مسبقاً",
    
    // Project Detail - Toast Messages
    accessDenied: "تم رفض الوصول",
    noPermissionToView: "ليس لديك إذن لعرض هذا المشروع",
    projectNotFound: "المشروع غير موجود",
    projectDoesNotExist: "المشروع الذي تبحث عنه غير موجود",
    failedToLoadProject: "فشل تحميل المشروع",
    noFileSelected: "لم يتم تحديد ملف",
    selectFileToUpload: "يرجى تحديد ملف مستند للرفع",
    documentUploaded: "تم رفع المستند",
    documentUploadedSuccess: "تم رفع المستند بنجاح",
    uploadFailed: "فشل الرفع",
    failedToUploadDocument: "فشل رفع المستند",
    documentDeleted: "تم حذف المستند",
    documentDeletedSuccess: "تم حذف المستند بنجاح",
    taskCompleted: "اكتملت المهمة",
    taskReopened: "تم إعادة فتح المهمة",
    taskMarkedAsDone: "عمل رائع! تم وضع علامة على المهمة كمكتملة.",
    taskMarkedAsIncomplete: "تم وضع علامة على المهمة كغير مكتملة.",
    taskDeleted: "تم حذف المهمة",
    taskDeletedMessage: "تم حذف المهمة بنجاح.",
    confirmDeleteTask: "هل أنت متأكد من أنك تريد حذف هذه المهمة؟",
    couldNotDeleteTask: "تعذر حذف المهمة. يرجى المحاولة مرة أخرى.",
    deleteTask: "حذف المهمة",
    updateFailed: "فشل التحديث",
    couldNotUpdateTask: "تعذر تحديث حالة المهمة",
    selectAtLeastOneFile: "يرجى تحديد ملف واحد على الأقل",
    fileTooLarge: "الملف كبير جداً",
    exceedsLimit: "يتجاوز حد 100 ميجابايت",
    filesUploadedSuccess: "تم رفع الملفات بنجاح",
    uploadedFiles: "تم الرفع",
    someFilesFailedToUpload: "فشل رفع بعض الملفات",
    selectAtLeastOneResearcher: "يرجى تحديد باحث واحد على الأقل للدعوة",
    collaboratorsInvited: "تمت دعوة المتعاونين",
    failedToInviteCollaborators: "فشلت دعوة المتعاونين",
    fileDeleted: "تم حذف الملف",
    successfullyDeleted: "تم الحذف بنجاح",
    collaboratorRemoved: "تمت إزالة المتعاون",
    successfullyRemoved: "تمت الإزالة بنجاح",
    removeFailed: "فشلت الإزالة",
    roleUpdated: "تم تحديث الدور",
    changedRoleTo: "تم تغيير الدور إلى",
    roleUpdateFailed: "فشل تحديث الدور",
    downloadFailed: "فشل التنزيل",
    unableToDownload: "تعذر تنزيل الملف",
    taskCreated: "تم إنشاء المهمة",
    taskAddedToProject: "تمت إضافة المهمة إلى المشروع",
    taskTitleRequired: "عنوان المهمة مطلوب",
    failedToCreateTask: "فشل إنشاء المهمة",
    notSpecified: "غير محدد",
    backToProjects: "العودة إلى المشاريع",
    settings: "الإعدادات",
    loadingProject: "جارٍ تحميل المشروع...",
    projectStatistics: "إحصائيات المشروع",
    teamMembers: "أعضاء الفريق",
    projectDocuments: "مستندات المشروع",
    uploadPreviewDocuments: "رفع ومعاينة مستندات Word و Excel و PowerPoint",
    projectTasks: "مهام المشروع",
    reopen: "إعادة فتح",
    done: "تم",
    due: "الموعد النهائي",
    invite: "دعوة",
    taskStatusDistribution: "توزيع حالة المهام",
    taskStatistics: "إحصائيات المهام",
    inProgress: "قيد التنفيذ",
    pending: "معلقة",
    totalTasks: "إجمالي المهام",
    taskProgressOverview: "نظرة عامة على تقدم المهام",
    projectTimelineTaskProgress: "الجدول الزمني للمشروع وتقدم المهام",
    visualRepresentation: "تمثيل بصري للمهام وحالة إنجازها",
    detailedTaskTimeline: "الجدول الزمني المفصل للمهام",
    selectResearchers: "اختيار الباحثين",
    inviteResearchersToCollaborate: "دعوة الباحثين للتعاون في هذا المشروع",
    collaboratorRole: "متعاون (يمكنه العرض والتحرير)",
    addNewTaskToTrack: "إضافة مهمة جديدة لتتبع تقدم المشروع",
    taskDescriptionPlaceholder: "وصف المهمة...",
    selectDocument: "اختيار المستند",
    noFileChosen: "لم يتم اختيار ملف",
    chooseFile: "اختيار ملف",
    uploadWordExcelPowerPoint: "رفع مستندات Word (.docx) أو Excel (.xlsx) أو PowerPoint (.pptx) للتحرير التعاوني",
    selectFilesToUpload: "اختيار الملفات للرفع",
    dragDropFiles: "اسحب وأفلت الملفات هنا أو انقر للتصفح",
    canManageTeam: "يمكنه إدارة الفريق",
    canOnlyView: "يمكنه العرض فقط",
    selected: "محدد",
    uploading: "جارٍ الرفع...",
    uploadResearchFiles: "رفع ملفات البحث لمشاركتها مع فريقك (حد أقصى 100 ميجابايت لكل ملف)",
    selectFiles: "اختيار الملفات",
    descriptionOptional: "الوصف (اختياري)",
    briefDescriptionFiles: "وصف موجز للملفات...",
    wordDocumentPreview: "معاينة مستند Word",
    previewMode: "وضع المعاينة - قم بالتنزيل لعرض المحتوى الكامل والتحرير في Microsoft Word أو التطبيقات المتوافقة",
    downloadToView: "تنزيل للعرض",
    collaborativeEditingComingSoon: "التحرير التعاوني قريباً",
    previewModeDownloadToEdit: "وضع المعاينة - قم بالتنزيل للتحرير في تطبيقات سطح المكتب",
    joined: "انضم",
    priorityLabel: "الأولوية",
    projectFiles: "ملفات المشروع",
    upload: "رفع",
    versions: "الإصدارات",
    owner: "مالك",
    collaborator: "متعاون",
    save: "حفظ",
    
    // Status translations for dynamic values
    todo: "مهام",
    "in progress": "قيد التنفيذ",
    completed: "مكتمل",
    high: "عالية",
    medium: "متوسطة",
    low: "منخفضة",
    
    // File preview and sharing
    comments: "التعليقات",
    viewing: "يشاهد",
    ready: "جاهز",
    shareFile: "مشاركة الملف",
    shareWithOtherResearchers: "مشاركة مع باحثين آخرين",
    shareLink: "رابط المشاركة",
    shareWithResearchers: "مشاركة مع الباحثين",
    messageOptional: "رسالة (اختياري)",
    shareWith: "مشاركة مع",
    researchers: "باحثين",
    
    // Research Library
    researchLibrary: "مكتبة البحث",
    uploadPDF: "رفع ملف PDF",
    searchPapers: "البحث في الأوراق...",
    files: "الملفات",
    myDocuments: "مستنداتي",
    sharedWithMe: "مشارك معي",
    sharedDocuments: "المستندات المشتركة",
    noDocumentsFound: "لم يتم العثور على مستندات",
    selectDocumentToView: "اختر مستنداً للعرض",
    invalidFile: "ملف غير صالح",
    pleaseSelectPDF: "يرجى اختيار ملف PDF",
    uploadSuccessful: "تم الرفع بنجاح",
    pdfProcessed: "تم معالجة ملف PDF واستخراج البيانات الوصفية",
    uploadFailed: "فشل الرفع",
    failedToUploadPDF: "فشل في رفع ملف PDF",
    networkError: "خطأ في الشبكة",
    summaryGenerated: "تم إنشاء الملخص",
    aiSummaryExtracted: "تم استخراج ملخص الذكاء الاصطناعي والكلمات المفتاحية",
    summarizationFailed: "فشل التلخيص",
    pleaseTryAgain: "يرجى المحاولة مرة أخرى",
    chatFailed: "فشلت المحادثة",
    citationGenerated: "تم إنشاء الاستشهاد",
    citationFailed: "فشل الاستشهاد",
    documentDeleted: "تم حذف المستند",
    deleteFailed: "فشل الحذف",
    sharingUpdated: "تم تحديث المشاركة",
    sharingFailed: "فشلت المشاركة",
    couldNotUpdateSharing: "تعذر تحديث إعدادات المشاركة",
    failedToUpdateSharing: "فشل في تحديث إعدادات المشاركة",
    invalidTitle: "عنوان غير صالح",
    titleCannotBeEmpty: "لا يمكن أن يكون العنوان فارغاً",
    documentRenamed: "تم إعادة تسمية المستند",
    renameFailed: "فشلت إعادة التسمية",
    failedToOpenDocument: "فشل في فتح المستند",
    copied: "تم النسخ!",
    
    // Library predefined questions
    generateSummary: "إنشاء ملخص لهذه الورقة",
    resultsOfPaper: "نتائج الورقة",
    conclusionsFromPaper: "استنتاجات من الورقة",
    explainAbstract: "شرح ملخص هذه الورقة",
    contributionsOfPaper: "ما هي مساهمات هذه الورقة",
    findRelatedPapers: "العثور على أوراق ذات صلة",
    explainPracticalImplications: "شرح الآثار العملية لهذه الورقة",
    summariseIntroduction: "تلخيص مقدمة هذه الورقة",
    literatureSurvey: "مسح الأدبيات لهذه الورقة",
    methodsUsed: "الطرق المستخدمة في هذه الورقة",
    dataUsedInPaper: "ما البيانات المستخدمة في هذه الورقة",
    limitationsOfPaper: "قيود هذه الورقة",
    futureWorks: "الأعمال المستقبلية المقترحة في هذه الورقة",
    
    // Library views
    tldr: "ملخص سريع",
    content: "المحتوى",
    chat: "محادثة",
    citation: "استشهاد",
    askAboutPaper: "اسأل عن هذه الورقة...",
    shareDocument: "مشاركة المستند",
    pdfViewer: "عارض PDF",
    generating: "جاري الإنشاء...",
    citationStyle: "نمط الاستشهاد",
    copyCitation: "نسخ الاستشهاد",
    privateDocument: "مستند خاص",
    onlyYouCanView: "يمكنك أنت فقط عرض هذا المستند",
    publicDocument: "مستند عام",
    anyoneWithLink: "يمكن لأي شخص لديه الرابط العرض",
    sharedWithTeam: "مشارك مع الفريق",
    projectCollaborators: "يمكن لمتعاوني المشروع العرض",
    researcherLibrary: "مكتبة الباحث",
    home: "الرئيسية",
    quickQuestions: "أسئلة سريعة",
    showMoreQuestions: "إظهار {count} أسئلة أخرى",
    selectQuestionOrType: "اختر سؤالاً أعلاه أو اكتب سؤالك الخاص",
    files: "الملفات",
    myDocuments: "مستنداتي",
    sharedWithMe: "مشارك معي",
    sharedDocuments: "المستندات المشتركة",
    noSharedDocumentsFound: "لم يتم العثور على مستندات مشتركة",
    generateFromCrossref: "إنشاء من Crossref",
    generateAISummary: "إنشاء ملخص بالذكاء الاصطناعي",
    generateSummary: "إنشاء ملخص",
    
    // Researcher Profiles & Research Assistants
    researcherProfiles: "ملفات الباحثين",
    researchAssistants: "مساعدو البحث",
    searchResearchers: "البحث عن باحثين...",
    searchAssistants: "البحث عن مساعدين...",
    noProfilesFound: "لم يتم العثور على ملفات",
    createYourProfile: "أنشئ ملفك الشخصي للتواصل مع باحثين آخرين",
    createProfile: "إنشاء ملف شخصي",
    editProfile: "تعديل الملف الشخصي",
    sendConnectionRequest: "إرسال طلب اتصال",
    connectionRequested: "تم طلب الاتصال",
    connected: "متصل",
    joinedVideoConference: "تم الانضمام بنجاح إلى مؤتمر الفيديو",
    messageOptional: "رسالة (اختياري)",
    introduceYourself: "عرّف بنفسك...",
    sendRequest: "إرسال الطلب",
    researchInterestsSkills: "الاهتمامات البحثية / المهارات",
    researchField: "مجال البحث",
    describeResearchField: "صف مجال بحثك...",
    userType: "نوع المستخدم",
    selectUserType: "اختر نوع المستخدم",
    researcher: "باحث",
    researchAssistant: "مساعد بحثي",
    both: "كلاهما",
    googleScholarURL: "رابط Google Scholar",
    orcidID: "معرف ORCID",
    viewGoogleScholar: "عرض Google Scholar",
    viewORCID: "عرض ORCID",
    
    // Profile Cards and Labels
    researchInterests: "الاهتمامات البحثية",
    email: "البريد الإلكتروني",
    phone: "الهاتف",
    location: "الموقع",
    loadingProfiles: "جارٍ تحميل ملفات الباحثين...",
    loadingAssistants: "جارٍ تحميل مساعدي البحث...",
    noResearchersFound: "لم يتم العثور على باحثين",
    noAssistantsFound: "لم يتم العثور على مساعدي البحث",
    adjustSearchCriteria: "جرب تعديل معايير البحث",
    createProfileToConnect: "أنشئ ملفك الشخصي للتواصل مع باحثين آخرين",
    university: "الجامعة",
    academicLevel: "المستوى الأكاديمي",
    sendMessage: "إرسال رسالة",
    message: "رسالة",
    viewProfile: "عرض الملف الشخصي",
    connectionPending: "الاتصال معلق",
    connect: "اتصل",
    activeResearchers: "الباحثون النشطون",
    activeAssistants: "مساعدو البحث النشطون",
    countriesRepresented: "البلدان الممثلة",
    researchFields: "مجالات البحث",
    researcherProfilesDesc: "تواصل مع الباحثين في جميع أنحاء العالم. تصفح الملفات، ابحث عن متعاونين، وابني شبكتك.",
    researchAssistantsDesc: "تواصل مع مساعدي البحث المؤهلين. تصفح الملفات، ابحث عن الدعم لمشاريعك، وابني فريقك.",
    editProfileBtn: "تعديل الملف الشخصي",
    
    // Connection Dialog
    sendConnectionRequestTo: "إرسال طلب اتصال إلى",
    connectWith: "اتصل مع",
    sendConnectionRequestMessage: "إرسال طلب اتصال لبدء التعاون",
    connectMessagePlaceholder: "مرحباً، أود التواصل معك لمناقشة التعاون المحتمل...",
    optional: "اختياري",
    
    // Edit Profile Dialog  
    editYourProfile: "تعديل ملفك الشخصي",
    updateProfileInformation: "تحديث معلومات ملفك الشخصي كباحث",
    profilePhoto: "صورة الملف الشخصي",
    updateProfile: "تحديث الملف الشخصي",
    name: "الاسم",
    country: "البلد",
    city: "المدينة",
    selectCountry: "اختر البلد",
    saveProfile: "حفظ الملف الشخصي",
    saving: "جارٍ الحفظ...",
    profileUpdated: "تم تحديث الملف الشخصي",
    profileUpdatedSuccess: "تم تحديث ملفك الشخصي بنجاح",
    profileUpdateFailed: "فشل تحديث الملف الشخصي",
    connectionRequestSent: "تم إرسال طلب الاتصال",
    requestSentSuccess: "تم إرسال طلب الاتصال الخاص بك",
    requestSentTo: "تم إرسال طلبك إلى",
    connectionRequestFailed: "فشل طلب الاتصال",
    failedToUpdateProfile: "فشل تحديث الملف الشخصي",
    failedToDeleteProfile: "فشل حذف الملف الشخصي",
    profileDeleted: "تم حذف الملف الشخصي",
    profileDeletedSuccess: "تم حذف ملفك الشخصي بنجاح",
    confirmDelete: "هل أنت متأكد أنك تريد حذف ملفك الشخصي؟ لا يمكن التراجع عن هذا الإجراء.",
    imageSizeTooLarge: "يجب أن يكون حجم الصورة أقل من 5 ميجابايت",
    cannotConnect: "لا يمكن الاتصال",
    cannotConnectWithYourself: "لا يمكنك الاتصال بنفسك",
    loginRequiredMessage: "يرجى تسجيل الدخول للاتصال بالباحثين",
    joinResearchCommunity: "انضم إلى مجتمعنا البحثي",
    createProfileCollaborate: "أنشئ ملفك الشخصي وابدأ التعاون مع الباحثين في جميع أنحاء العالم",
    
    expectedEnd: "تاريخ الانتهاء المتوقع",
    uploadFiles: "رفع الملفات",
    inviteCollaborators: "دعوة متعاونين",
    createTask: "إنشاء مهمة",
    
    // Agreements
    agreements: "الاتفاقيات",
    nda: "اتفاقية عدم إفصاح",
    rca: "اتفاقية تعاون بحثي",
    nonDisclosureAgreement: "اتفاقية عدم إفصاح (NDA)",
    researchCollaborationAgreement: "اتفاقية تعاون بحثي (RCA)",
    signNDA: "توقيع اتفاقية عدم الإفصاح",
    signRCA: "توقيع اتفاقية التعاون البحثي",
    setupRCA: "إعداد اتفاقية التعاون البحثي",
    viewAgreements: "عرض الاتفاقيات",
    agreementsSigned: "الاتفاقيات الموقعة",
    agreementContent: "محتوى الاتفاقية",
    iAgree: "أوافق",
    iHaveReadAndAgree: "لقد قرأت ووافقت على الشروط أعلاه",
    signAgreement: "توقيع الاتفاقية",
    downloadPDF: "تحميل PDF",
    signedBy: "وقعه",
    signedOn: "تاريخ التوقيع",
    digitalFingerprint: "البصمة الرقمية",
    notSigned: "غير موقع",
    projectOwner: "مالك المشروع",
    collaborator: "متعاون",
    fillRCADetails: "ملء تفاصيل اتفاقية التعاون البحثي",
    projectTitle: "عنوان المشروع",
    researchObjectives: "الأهداف البحثية",
    expectedContributions: "المساهمات المتوقعة",
    mainResearcherAndCollaborators: "الباحث الرئيسي والمتعاونين",
    saveRCATemplate: "حفظ نموذج الاتفاقية",
    rcaTemplateRequired: "يجب على مالك المشروع ملء نموذج الاتفاقية قبل أن يتمكن المتعاونون من التوقيع",
    bothPartiesMustSign: "يجب على كل من المالك والمتعاونين التوقيع",
    agreementAlreadySigned: "لقد وقعت هذه الاتفاقية بالفعل",
    note: "ملاحظة",
    
    // Admin Panel
    adminDashboard: "لوحة تحكم المسؤول",
    welcomeBack: "مرحباً بعودتك",
    backToSite: "العودة للموقع",
    users: "المستخدمون",
    subscriptions: "الاشتراكات",
    reports: "التقارير",
    totalUsers: "إجمالي المستخدمين",
    activeSubscriptions: "الاشتراكات النشطة",
    monthlyRevenue: "الإيرادات الشهرية",
    totalProjects: "إجمالي المشاريع",
    quickActions: "إجراءات سريعة",
    manageUsers: "إدارة المستخدمين",
    manageSubscriptions: "إدارة الاشتراكات",
    viewReports: "عرض التقارير",
    platformStatistics: "إحصائيات المنصة",
    totalResearchers: "إجمالي الباحثين",
    totalDocuments: "إجمالي المستندات",
    adminUsers: "مستخدمو المسؤول",
    userManagement: "إدارة المستخدمين",
    searchByName: "البحث بالاسم أو البريد الإلكتروني...",
    search: "بحث",
    allUsers: "جميع المستخدمين",
    role: "الدور",
    status: "الحالة",
    created: "تاريخ الإنشاء",
    actions: "الإجراءات",
    active: "نشط",
    inactive: "غير نشط",
    activate: "تفعيل",
    deactivate: "إلغاء التفعيل",
    deleteUser: "حذف المستخدم",
    areYouSure: "هل أنت متأكد؟",
    deleteUserConfirm: "سيؤدي هذا إلى حذف المستخدم وجميع بياناته بشكل دائم. لا يمكن التراجع عن هذا الإجراء.",
    cancel: "إلغاء",
    subscriptionManagement: "إدارة الاشتراكات",
    addSubscription: "إضافة اشتراك",
    allSubscriptions: "جميع الاشتراكات",
    plan: "الخطة",
    type: "النوع",
    price: "السعر",
    endDate: "تاريخ الانتهاء",
    user: "المستخدم",
    addNewSubscription: "إضافة اشتراك جديد",
    selectUser: "اختر مستخدم",
    billingType: "نوع الفوترة",
    startDate: "تاريخ البدء",
    createSubscription: "إنشاء اشتراك",
    reportsAnalytics: "التقارير والتحليلات",
    userGrowth: "نمو المستخدمين",
    revenueAnalytics: "تحليلات الإيرادات",
    totalRevenue: "إجمالي الإيرادات",
    revenueByPlan: "الإيرادات حسب الخطة",
    contentStatistics: "إحصائيات المحتوى",
    platformHealth: "صحة المنصة",
    avgProjectsPerUser: "متوسط المشاريع لكل مستخدم",
    avgDocumentsPerUser: "متوسط المستندات لكل مستخدم",
    subscriptionRate: "معدل الاشتراك",
    recentSignups: "التسجيلات الأخيرة",
    
    // Notification Messages
    welcome: "مرحباً",
    loginSuccessful: "تم تسجيل الدخول بنجاح",
    loginFailed: "فشل تسجيل الدخول",
    signupFailed: "فشل التسجيل",
    profileUpdateSuccess: "تم تحديث الملف الشخصي بنجاح",
    profileUpdateFailed: "فشل تحديث الملف الشخصي",
    paymentSuccess: "تم الدفع بنجاح",
    paymentFailed: "فشل الدفع",
    subscriptionActivated: "تم تفعيل الاشتراك",
    accessDenied: "تم رفض الوصول",
    adminPrivilegesRequired: "يتطلب امتيازات المسؤول",
    userDeleted: "تم حذف المستخدم بنجاح",
    subscriptionCreated: "تم إنشاء الاشتراك بنجاح",
    errorOccurred: "حدث خطأ",
    
    // Team Matching
    findYourResearchTeam: "ابحث عن فريقك البحثي",
    teamMatchingDescription: "مطابقة مدعومة بالذكاء الاصطناعي لربطك بالباحثين الذين يشاركونك اهتماماتك",
    recommendedResearchTeams: "الفرق البحثية الموصى بها",
    recommendedResearchers: "الباحثون الموصى بهم",
    suggestedResearchTopics: "موضوعات البحث المقترحة",
    teamMembers: "أعضاء الفريق",
    teamStrengths: "نقاط قوة الفريق",
    connectWith: "تواصل مع",
    sendConnectionRequestMessage: "أرسل طلب تواصل لبدء التعاون",
    connectMessagePlaceholder: "مرحباً، وجدت ملفك الشخصي من خلال مطابقة الفرق وأود التواصل للتعاون البحثي المحتمل...",
    teamMatchingConnectDefaultMessage: "وجدت ملفك الشخصي من خلال مطابقة الفرق وأود التواصل للتعاون البحثي المحتمل.",
    noMatchesFound: "لم يتم العثور على تطابقات",
    beFirstToJoin: "كن من الأوائل! المزيد من الباحثين ينضمون إلى نظام المطابقة.",
    adjustFilters: "حاول تعديل الفلاتر أو تحقق لاحقاً مع انضمام المزيد من الباحثين.",
    researchersActiveInMatching: "باحثون نشطون حالياً في المطابقة",
    inviteColleagues: "ادعُ زملاءك للانضمام وتوسيع شبكتك!",
    match: "تطابق",
    field: "المجال",
    interests: "الاهتمامات",
    optIn: "الاشتراك",
    optOut: "إلغاء الاشتراك",
    sending: "جارِ الإرسال...",
    
    // Dashboard Team Matching Tab
    yourMatches: "تطابقاتك",
    teamSuggestions: "اقتراحات الفرق",
    status: "الحالة",
    active: "نشط",
    recommendedTeams: "الفرق الموصى بها",
    team: "فريق",
    members: "أعضاء",
    topResearcherMatches: "أفضل تطابقات الباحثين",
    viewAllMatchesAndTeams: "عرض جميع التطابقات والفرق",
    noMatchesFoundYet: "لم يتم العثور على تطابقات بعد",
    findMatches: "البحث عن تطابقات",
    findTeam: "البحث عن فريق",
    
    // Team Strengths (from backend)
    diverseAcademicPerspectives: "وجهات نظر أكاديمية متنوعة",
    interdisciplinaryExpertise: "خبرة متعددة التخصصات",
    focusedDomainKnowledge: "معرفة متخصصة في المجال",
    strongResearchAlignment: "توافق بحثي قوي",
    internationalCollaboration: "تعاون دولي",
    complementarySkills: "مهارات متكاملة",
    
    // Match Reasons (from backend)
    strongResearchInterestAlignment: "توافق قوي في الاهتمامات البحثية",
    goodResearchInterestMatch: "تطابق جيد في الاهتمامات البحثية",
    sameField: "نفس المجال",
    localCollaborationOpportunity: "فرصة للتعاون المحلي",
    complementaryExpertiseLevels: "مستويات خبرة متكاملة",
    similarResearchProfile: "ملف بحثي مشابه",
    prefersCollaboration: "يفضل التعاون",
    
    // Team Matching Page
    findYourResearchTeamTitle: "ابحث عن فريقك البحثي",
    aiPoweredMatchingDescription: "مطابقة مدعومة بالذكاء الاصطناعي لربطك بالباحثين الذين يشاركونك اهتماماتك",
    optedIn: "مشترك",
    notOptedIn: "غير مشترك",
    activeInMatching: "نشط في المطابقة",
    totalResearchers: "إجمالي الباحثين",
    refineYourSearch: "تحسين البحث",
    anyLanguage: "أي لغة",
    anyType: "أي نوع",
    allCountries: "جميع الدول",
    allLevels: "جميع المستويات",
    allFields: "جميع المجالات",
    clearFilters: "مسح الفلاتر",
    searchMatches: "البحث عن تطابقات",
    recommendedResearchTeamsCount: "الفرق البحثية الموصى بها",
    teamMatchingSettings: "إعدادات مطابقة الفرق",
    controlVisibility: "التحكم في ظهورك في نظام مطابقة الفرق",
    privacyNote: "ملفك الشخصي مرئي فقط للباحثين المشتركين الآخرين.",
    canOptOutAnytime: "يمكنك إلغاء الاشتراك في أي وقت.",
    currentStatus: "الحالة الحالية",
    researchersWithSynergy: "باحثون بتوافق",
    synergy: "توافق",
    joinMatching: "الانضمام للمطابقة",
    optInDescription: "اشترك لاكتشاف الباحثين ذوي الاهتمامات المشابهة والعثور على المتعاونين البحثيين المثاليين. ظهور ملفك الشخصي يتم التحكم فيه بواسطتك.",
    privacy: "الخصوصية",
    academicLevel: "المستوى الأكاديمي",
    collaboration: "التعاون",
    local: "محلي",
    internationalLabel: "دولي",
    languageFilter: "اللغة",
    
    // Landing Page Team Card
    semanticInterestMatching: "مطابقة الاهتمامات الدلالية",
    complementaryExpertise: "الخبرة التكميلية",
    smartRecommendations: "توصيات ذكية",
    privacyControlledVisibility: "رؤية محكومة بالخصوصية",
    discoverMatches: "اكتشف التطابقات",
    
    // Notifications
    newMessage: "رسالة جديدة",
    connectionAccepted: "تم قبول الاتصال",
    connectionRequest: "طلب اتصال",
    newAnnouncement: "إعلان جديد",
    acceptedYourConnectionRequest: "قبل طلب الاتصال الخاص بك",
    sentYouConnectionRequest: "أرسل لك طلب اتصال",
    new: "جديد",
    
    // Team Matching - Roles and Actions
    leadResearcher: "الباحث الرئيسي",
    coResearcher: "باحث مشارك",
    collaborator: "متعاون",
    connectWithTeam: "التواصل مع الفريق",
    teamContact: "التواصل مع الفريق",
    contactTeamMembersIndividually: "تواصل مع أعضاء الفريق بشكل فردي من خلال ملفاتهم الشخصية",
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isRTL: language === 'ar'
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
